import "antd/dist/reset.css";
import React, { useEffect, useLayoutEffect, useState, useRef, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import "../../../popups.css";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { getApi, postApi, putApi } from "../../../Services/apiService";
import CircularProgress from "@mui/material/CircularProgress";
import { toast } from "react-toastify";
import {
  convertUTCToDate,
  convertUTCToTime,
  eventDateForApi,
  eventTimeForApi,
} from "../../../Utils/dateUtils";
import { renderNameWithColor } from "../../../Utils/colors";
import DropdownSelect from "../../FamilyTree/DropdownSelect";
import { sortedStates } from "../../../Utils/family-tree/dropdown";
import { calculateAge } from "../../../Utils/family-tree/dateAndTime";
import { getFamilyTreeIdFromStorage } from "../../../Utils/headOfLineRole";
import { isLoggedInUserTreeAdminFromStorage } from "../../../Utils/subscriptionAccess";
import {
  formatDashboardFamilyLineHeadName,
  formatDashboardFullPersonName,
  shouldShowDashboardLastName,
} from "../../../Utils/dashboardMemberDisplayName";
import { formatSuffix, getMemberSuffix } from "../../../Utils/name";
import {
  FilterTriggerLabel,
  filterTriggerClass,
  formatFilterValueList,
} from "../../../Utils/filterTriggerLabel";

/** Earliest calendar day allowed for event start (today + this many days). */
const MIN_EVENT_LEAD_DAYS = 4;
/** RSVP deadline must be at least this many calendar days before event start. */
const MIN_CALENDAR_DAYS_RSVP_BEFORE_EVENT = 3;
/**
 * If /event-types does not yet include Misc, merge this row so the UI can offer it.
 * The backend must expose the same UUID as an event type for creates to persist.
 */
const MISC_NO_DATE_RANGE_EVENT_TYPE_ID = "c4a8f9e2-6d1b-4e9f-a7c3-9b2e8f1d4a60";

/**
 * Normalize API member gender: 1 male, 2 female, 3 other. The member forms
 * store Other as 4 (see sortedGenderOptions) and older rows carry 3; both land
 * in the "Other" bucket here.
 */
function getMemberGenderCode(member) {
  const g = member?.gender ?? member?.sex;
  const n = Number(g);
  if (n === 1 || n === 2) return String(n);
  if (n === 3 || n === 4) return "3";
  if (typeof g === "string") {
    const t = g.trim().toLowerCase();
    if (t === "male" || t === "m") return "1";
    if (t === "female" || t === "f") return "2";
    if (t === "other" || t === "binary" || t === "non-binary") return "3";
  }
  return null;
}

const GENDER_FILTER_LABELS = { 1: "Male", 2: "Female", 3: "Other" };

function normalizeInvitationRecord(invitedMember) {
  if (!invitedMember || typeof invitedMember !== "object") return null;
  const userId = invitedMember.user_id || invitedMember.user?.user_id || null;
  const invitationId =
    invitedMember.invitation_id ||
    (invitedMember.id &&
    userId &&
    String(invitedMember.id) !== String(userId)
      ? invitedMember.id
      : null);
  if (!userId && !invitationId) return null;
  return {
    user_id: userId,
    invitation_id: invitationId,
    note: invitedMember.note || "",
  };
}

function memberInvitationSnapshot(member) {
  const userId = member?.user_id || member?.user?.user_id || null;
  const invitationId = member?.invitation_id || null;
  if (!userId && !invitationId) return null;
  return {
    user_id: userId,
    invitation_id: invitationId,
    note: member?.note || "",
  };
}

function buildEditInvitationPayload(selectedMembers, priorInvitations = []) {
  const priorByUserId = new Map();
  priorInvitations.forEach((inv) => {
    if (inv?.user_id) {
      priorByUserId.set(String(inv.user_id), inv);
    }
  });

  const selectedUserIds = new Set();
  const invitedMembersArray = [];

  (selectedMembers || []).forEach((member) => {
    const userId = member?.user_id || member?.user?.user_id;
    if (!userId) return;

    selectedUserIds.add(String(userId));
    const prior = priorByUserId.get(String(userId));
    const payload = {
      user_id: userId,
      note: member.note || "",
    };
    const invitationId = member.invitation_id || prior?.invitation_id;
    if (invitationId) {
      payload.invitation_id = invitationId;
    }
    invitedMembersArray.push(payload);
  });

  const removedInvitedMembers = priorInvitations
    .filter(
      (inv) =>
        inv?.user_id &&
        inv?.invitation_id &&
        !selectedUserIds.has(String(inv.user_id))
    )
    .map((inv) => ({
      user_id: inv.user_id,
      invitation_id: inv.invitation_id,
    }));

  return { invitedMembersArray, removedInvitedMembers };
}

// Invitations are keyed by user_id, so members with no account (babies,
// in-laws who never signed up) can be listed and ticked but are not sent.
function warnMembersWithoutAccount(selectedMembers) {
  const count = (selectedMembers || []).filter(
    (member) => !(member?.user_id || member?.user?.user_id)
  ).length;
  if (count > 0) {
    toast.warning(
      `${count} selected member${count === 1 ? " has" : "s have"} no FamConnect account and won't receive an invitation.`
    );
  }
}

function appendEditInvitationFields(
  formData,
  selectedMembers,
  priorInvitations,
  includeRemovedInvites = false
) {
  const { invitedMembersArray, removedInvitedMembers } = buildEditInvitationPayload(
    selectedMembers,
    priorInvitations
  );
  warnMembersWithoutAccount(selectedMembers);

  formData.append("invited_members", JSON.stringify(invitedMembersArray));

  if (includeRemovedInvites && removedInvitedMembers.length > 0) {
    formData.append(
      "removed_invited_members",
      JSON.stringify(removedInvitedMembers)
    );
  }

  return { invitedMembersArray, removedInvitedMembers };
}

const { RangePicker } = DatePicker;

// Capitalizes the first letter of every word so an event title typed as
// "witherspoon red line retreat" reads "Witherspoon Red Line Retreat" without
// the user reaching for shift. Only lowercase letters are raised, so acronyms
// the user typed in caps (BBQ) are left alone, and the string length never
// changes so the caret stays put while typing.
const toEventTitleCase = (value) =>
  value.replace(
    /(^|[\s\-/([{"'])([a-z])/g,
    (match, prefix, letter) => prefix + letter.toUpperCase()
  );

function CreateEventModal({ isOpen, onClose, eventDetails, editMode, family_tree_id }) {
  const hasInitializedMembers = useRef(false);
  const isInitialLoad = useRef(true);
  const [membersFetchComplete, setMembersFetchComplete] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [isCreator, setIsCreator] = useState(false);
  // Invite table Email column is admin-only (mobile CSS hides it for everyone).
  const showEmailColumn = useMemo(() => isLoggedInUserTreeAdminFromStorage(), []);
  const [form, setForm] = useState({
    host: "",
    coHost: "",
    title: "",
    type: "",
    venue: "",
    date: "",
    startDate: "",
    endDate: "",
    time: "",
    address: "",
    location: "",
    state: "",
    rsvp: "",
    description: "",
    banner: "",
    event_type_name: "",
  });

  const [searchMember, setSearchMember] = useState("");
  const [dateRange, setDateRange] = useState([]);
  const [selectingStart, setSelectingStart] = useState(true);
  const [formInitialized, setFormInitialized] = useState(false);
  const [showFamilyLineColors, setShowFamilyLineColors] = useState(false);

  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [isHoveringBanner, setIsHoveringBanner] = useState(false);
  const [selectedDocumentFile, setSelectedDocumentFile] = useState(null);
  const [eventTypes, setEventTypes] = useState([]);

  const [loading, setLoading] = useState(false);
  const [draftLoading, setDraftLoading] = useState(false);

  //State for the member from the invitation
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [initiallyInvitedMembers, setInitiallyInvitedMembers] = useState([]);
  const [currentUserMember, setCurrentUserMember] = useState(null);

  //State for the age range filter
  const [showAgeRange, setShowAgeRange] = useState(false);
  const [ageFrom, setAgeFrom] = useState("");
  const [ageTo, setAgeTo] = useState("");
  // Range the list is currently narrowed to (the inputs above are just drafts
  // until Apply), so the trigger can read "Age Range: 18–40".
  const [appliedAgeRange, setAppliedAgeRange] = useState(null);

  //State for the color filter
  const [showFamilyColor, setShowFamilyColor] = useState(false);
  const [familyColorList, setFamilyColorList] = useState([]);
  const [selectedFamilyColor, setSelectedFamilyColor] = useState([]);
  const [appliedFamilyColor, setAppliedFamilyColor] = useState([]);

  //State for the gender filter
  const [showGender, setShowGender] = useState(false);
  const [selectedGender, setSelectedGender] = useState("");
  const [appliedGender, setAppliedGender] = useState("");

  //State for the "selected members" filter (match the table against the
  //invited-members list on the left)
  const [showSelection, setShowSelection] = useState(false);
  const [selectedSelectionFilter, setSelectedSelectionFilter] = useState("");
  const [appliedSelectionFilter, setAppliedSelectionFilter] = useState("");

  // Sorting State - default to name ascending so list is alphabetically ordered initially
  const [sortBy, setSortBy] = useState('name'); // 'name' or 'familyLine'
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'

  // Ref for the dropdown close
  const ageRangeRef = useRef(null);
  const famRef = useRef(null);
  // Line/Color popup floats above the modal (portaled) so it can be dragged
  // and always opens with its Apply button on screen.
  const famPopupRef = useRef(null);
  const famPopupDragRef = useRef(null);
  const [famPopupPos, setFamPopupPos] = useState(null);
  const genderRef = useRef(null);
  const selectionRef = useRef(null);
  // Snapshot of event details when opening in Edit mode (for is_edit comparison; excludes invite members)
  const initialEditDetailsRef = useRef(null);
  const formWasResetRef = useRef(false);
  const preResetInvitedMembersRef = useRef([]);
  const [eventAssetsCleared, setEventAssetsCleared] = useState(false);

  // Mobile: the invited-members summary moves out of the left column and sits
  // below the event input fields (matches the create-event mobile media query).
  const [isMobileView, setIsMobileView] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(max-width: 768px)").matches
  );

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const apply = () => setIsMobileView(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  // Load current user info and creator role once
  useEffect(() => {
    try {
      const storedUser = JSON.parse(localStorage.getItem("userDetail") || "{}");
      const storedRole = JSON.parse(
        localStorage.getItem("loggedIn_user_role") || "{}"
      );
      const userId =
        storedUser?.user_id ||
        storedUser?.id ||
        storedRole?.user_id ||
        storedRole?.id ||
        null;
      setCurrentUserId(userId);
    } catch {
      setCurrentUserId(null);
    }

    try {
      const storedRole = JSON.parse(localStorage.getItem("loggedIn_user_role") || "{}");
      setIsCreator(storedRole?.is_admin === true);
    } catch {
      setIsCreator(false);
    }
  }, []);

  // Prefill data when the editMode is true
  // useEffect(() => {

  //   if ((editMode === "Edit" || editMode === "View") && eventDetails && Object.keys(eventDetails).length > 0){
  //     // Wait for eventTypes to load before determining if it's a range event
  //     const isRange = eventTypes.length > 0 ? shouldUseRangePicker(eventDetails.type) : false;

  //     const newForm = {
  //       title: eventDetails.title || "",
  //       type: eventDetails.type || eventDetails.event_type_id || "",
  //       location: eventDetails.location || "",
  //       rsvp: eventDetails.rsvp_deadline || eventDetails.Rsvp_deadline || eventDetails.rsvp || "",
  //       description: eventDetails.description || "",
  //       banner: eventDetails.image || "", // Not used for preview, but good practice
  //       event_type_name: eventDetails.event_type_name || "",

  //       // Use event_time or time for the time field
  //       time: eventDetails.event_time || eventDetails.time || "",

  //       // Initialize date fields based on event type
  //       date: "",
  //       startDate: "",
  //       endDate: "",
  //     };

  //     // Only set date fields if eventTypes are loaded and we can determine the type
  //     if (eventTypes.length > 0) {
  //       if (isRange) {
  //         // Range event logic: API sends start_date/end_date
  //         const startDateStr = eventDetails.start_date || eventDetails.startDate || "";
  //         const endDateStr = eventDetails.end_date || eventDetails.endDate || "";

  //         newForm.startDate = startDateStr;
  //         newForm.endDate = endDateStr;

  //         // Manually initialize dateRange here to ensure the RangePicker is populated
  //         const start = dayjs(startDateStr);
  //         const end = dayjs(endDateStr);

  //         if (start.isValid() && end.isValid()) {
  //             setDateRange([start, end]);
  //         } else {
  //             setDateRange([]);
  //         }

  //       } else {
  //         // Single date event logic: API sends date as start_date
  //         newForm.date = eventDetails.date || eventDetails.start_date || eventDetails.startDate || "";
  //         setDateRange([]); // Clear range picker state just in case
  //       }
  //     } else {
  //       // Fallback: try to determine from available date fields
  //       if (eventDetails.start_date && eventDetails.end_date) {
  //         newForm.startDate = eventDetails.start_date;
  //         newForm.endDate = eventDetails.end_date;
  //         const start = dayjs(eventDetails.start_date);
  //         const end = dayjs(eventDetails.end_date);
  //         if (start.isValid() && end.isValid()) {
  //           setDateRange([start, end]);
  //         }
  //       } else {
  //         newForm.date = eventDetails.date || eventDetails.start_date || eventDetails.startDate || "";
  //       }
  //     }

  //     setForm(newForm);

  //     // Also, set the preview URL if a banner is present
  //     if (eventDetails.image) {
  //         setPreviewUrl(eventDetails.image);
  //     }
  //   } else {
  //     // When user add the event
  //     setForm({
  //       title: "",
  //       type: "",
  //       date: "",
  //       startDate: "",
  //       endDate: "",
  //       time: "",
  //       location: "",
  //       rsvp: "",
  //       description: "",
  //       banner: "",
  //       event_type_name: "",
  //     });
  //     setSelectedMembers([]);
  //     setPreviewUrl("");
  //     setDateRange([]);
  //   }
  //   setFormInitialized(true);
  //   fetchEventTypes();
  // }, [editMode, eventDetails, isOpen, eventTypes.length]);

  useEffect(() => {
    fetchEventTypes();
  }, []);

  // Prefill data when the editMode is true
  // Prefill data when the editMode is true
useEffect(() => {
  const hasEventData =
    (editMode === "Edit" || editMode === "View") &&
    eventDetails &&
    Object.keys(eventDetails).length > 0;

  if (formWasResetRef.current && editMode === "Edit") {
    return;
  }

  if (hasEventData) {
    const eventTypeId = eventDetails.type || eventDetails.event_type_id || "";
    const isRangeBasedOnType =
      eventTypes.length > 0 ? shouldUseRangePicker(eventTypeId) : false;

    // ---- Convert UTC → Normal Formats ----
    const convertedStartDate = eventDetails.start_date
      ? convertUTCToDate(eventDetails.start_date)
      : "";

    const convertedEndDate = eventDetails.end_date
      ? convertUTCToDate(eventDetails.end_date)
      : "";

    // For single-date events, always use start_date if date field doesn't exist
    // This ensures single-date events always have a date value
    let convertedSingleDate = "";
    if (eventDetails.date) {
      convertedSingleDate = convertUTCToDate(eventDetails.date);
    }
    // Always fallback to start_date for single-date events
    if (!convertedSingleDate || convertedSingleDate.trim() === "") {
      convertedSingleDate = convertedStartDate || "";
    }

    const convertedTime = eventDetails.event_time
      ? convertUTCToTime(eventDetails.event_time)
      : "";

    // Convert RSVP deadline from UTC to date format
    const convertedRsvp = eventDetails.Rsvp_deadline || eventDetails.rsvp_deadline || eventDetails.rsvp || "";
    const convertedRsvpDate = convertedRsvp ? convertUTCToDate(convertedRsvp) : "";

    // New form base state
    // Combine location, state into address field for display
    const combinedAddress = eventDetails.location 
      ? (eventDetails.state ? `${eventDetails.location}, ${eventDetails.state}` : eventDetails.location)
      : "";
    
    const newForm = {
      host: eventDetails.host || eventDetails.host_id || "",
      coHost: eventDetails.co_host || eventDetails.co_host_id || "",
      title: eventDetails.title || "",
      type: eventTypeId,
      venue: eventDetails.venue || "",
      address: combinedAddress,
      location: eventDetails.location || "",
      state: eventDetails.state || "",
      rsvp: convertedRsvpDate,
      description: eventDetails.description || "",
      banner: eventDetails.image || "",
      event_type_name: eventDetails.event_type_name || "",
      time: convertedTime,
      date: "", // Initialize as empty
      startDate: "",
      endDate: "",
    };

    // --- DATE PREFILL LOGIC ---
    const hasRangeDatesInAPI = eventDetails.start_date && eventDetails.end_date;

    if (eventTypes.length > 0) {
      const shouldUseRange = hasRangeDatesInAPI || isRangeBasedOnType;

      if (shouldUseRange) {
        // --- RANGE EVENT LOGIC ---
        newForm.startDate = convertedStartDate;
        newForm.endDate = convertedEndDate;

        const start = dayjs(convertedStartDate, "YYYY-MM-DD");
        const end = dayjs(convertedEndDate, "YYYY-MM-DD");

        if (start.isValid() && end.isValid()) {
          setTimeout(() => {
            setDateRange([start, end]);
          }, 0);
        } else {
          setDateRange([]);
        }
      } else {
        // --- SINGLE DATE EVENT LOGIC ---
        // CRITICAL FIX: Always set date for single-date events
        // Use convertedSingleDate which already falls back to convertedStartDate
        newForm.date = convertedSingleDate || convertedStartDate || "";
        
        // Also set startDate for API consistency (since API expects start_date)
        newForm.startDate = convertedStartDate;
        
        setDateRange([]); // Clear range picker state
      }
    } else if (hasRangeDatesInAPI) {
      // If eventTypes haven't loaded but we have both dates, assume range
      newForm.startDate = convertedStartDate;
      newForm.endDate = convertedEndDate;

      const start = dayjs(convertedStartDate, "YYYY-MM-DD");
      const end = dayjs(convertedEndDate, "YYYY-MM-DD");

      if (start.isValid() && end.isValid()) {
        setTimeout(() => {
          setDateRange([start, end]);
        }, 0);
      } else {
        setDateRange([]);
      }
    } else {
      // Fallback: use start_date as single date (when eventTypes not loaded yet)
      // CRITICAL FIX: Always set date for single-date events
      newForm.date = convertedSingleDate || convertedStartDate || "";
      newForm.startDate = convertedStartDate;
      setDateRange([]);
    }

    setForm(newForm);

    if (eventDetails.image) {
      setPreviewUrl(eventDetails.image);
    }
    setSelectedDocumentFile(null);

    // Snapshot for is_edit: only event details (excludes invite members)
    initialEditDetailsRef.current = {
      host: newForm.host || "",
      coHost: newForm.coHost || "",
      title: newForm.title || "",
      type: newForm.type || "",
      venue: newForm.venue || "",
      address: newForm.address || "",
      description: newForm.description || "",
      rsvp: newForm.rsvp || "",
      time: newForm.time || "",
      date: newForm.date || "",
      startDate: newForm.startDate || "",
      endDate: newForm.endDate || "",
      hasImage: !!eventDetails.image,
      hasDocument: !!eventDetails.document,
    };

    setTimeout(() => {
      isInitialLoad.current = false;
    }, 100);
  } else {
    // Reset form for "Add" mode
    initialEditDetailsRef.current = null;
    setForm({
      host: "",
      coHost: "",
      title: "",
      type: "",
      venue: "",
      date: "",
      startDate: "",
      endDate: "",
      time: "",
      address: "",
      location: "",
      state: "",
      rsvp: "",
      description: "",
      banner: "",
      event_type_name: "",
    });
    setSelectedMembers([]);
    setInitiallyInvitedMembers([]);
    setPreviewUrl("");
    setSelectedFile(null);
    setSelectedDocumentFile(null);
    setDateRange([]);
    hasInitializedMembers.current = false;
    isInitialLoad.current = true;
  }
}, [editMode, eventDetails, eventTypes]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ageRangeRef.current && !ageRangeRef.current.contains(e.target)) {
        setShowAgeRange(false);
      }
      if (
        famRef.current &&
        !famRef.current.contains(e.target) &&
        !famPopupRef.current?.contains(e.target)
      ) {
        setShowFamilyColor(false);
      }
      if (genderRef.current && !genderRef.current.contains(e.target)) {
        setShowGender(false);
      }
      if (selectionRef.current && !selectionRef.current.contains(e.target)) {
        setShowSelection(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Open the Line/Color popup under its trigger, lifted as far as needed so
  // the whole popup (Clear/Apply included) fits in the viewport.
  useLayoutEffect(() => {
    if (!showFamilyColor) {
      setFamPopupPos(null);
      return;
    }
    const trigger = famRef.current;
    const popup = famPopupRef.current;
    if (!trigger || !popup) return;
    const margin = 8;
    const rect = trigger.getBoundingClientRect();
    const width = popup.offsetWidth;
    const height = Math.min(popup.offsetHeight, window.innerHeight - margin * 2);
    setFamPopupPos({
      left: Math.max(margin, Math.min(rect.left, window.innerWidth - width - margin)),
      top: Math.max(margin, Math.min(rect.bottom + 4, window.innerHeight - height - margin)),
    });
  }, [showFamilyColor, familyColorList.length]);

  const beginFamPopupDrag = (e) => {
    const popup = famPopupRef.current;
    if (!popup) return;
    const rect = popup.getBoundingClientRect();
    famPopupDragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
    e.preventDefault();
  };

  const moveFamPopup = (e) => {
    const d = famPopupDragRef.current;
    if (!d) return;
    const left = d.left + e.clientX - d.startX;
    const top = d.top + e.clientY - d.startY;
    setFamPopupPos({
      left: Math.max(0, Math.min(left, window.innerWidth - d.width)),
      top: Math.max(0, Math.min(top, window.innerHeight - d.height)),
    });
  };

  const endFamPopupDrag = () => {
    famPopupDragRef.current = null;
  };

  // ===== Draggable & resizable modal card =====
  const modalNodeRef = useRef(null);
  const modalHeaderRef = useRef(null);
  const interactionRef = useRef(null); // active drag/resize gesture
  const isDetachedRef = useRef(false);
  const minSizeRef = useRef({ w: 0, h: 0 });

  // Switch the card from its default (flex-centered) layout to explicit fixed
  // positioning the first time the user drags or resizes it. Captured at its
  // current on-screen rect so there is NO visual jump: the default size and
  // position are preserved exactly until the user interacts.
  const detachModalCard = useCallback(() => {
    const node =
      modalNodeRef.current ||
      modalHeaderRef.current?.closest(".ReactModal__Content") ||
      null;
    if (!node) return null;
    modalNodeRef.current = node;

    if (!isDetachedRef.current) {
      const rect = node.getBoundingClientRect();
      minSizeRef.current = { w: rect.width, h: rect.height };
      node.style.position = "fixed";
      node.style.margin = "0";
      node.style.inset = "auto";
      node.style.left = `${rect.left}px`;
      node.style.top = `${rect.top}px`;
      node.style.width = `${rect.width}px`;
      node.style.height = `${rect.height}px`;
      node.style.maxWidth = "none";
      node.style.maxHeight = "none";
      node.style.transform = "none";
      node.style.overflow = "hidden";
      node.style.boxSizing = "border-box";
      isDetachedRef.current = true;
    }
    return node;
  }, []);

  const handleGestureMove = useCallback((e) => {
    const g = interactionRef.current;
    const node = modalNodeRef.current;
    if (!g || !node) return;

    const dx = e.clientX - g.startX;
    const dy = e.clientY - g.startY;
    const minW = minSizeRef.current.w;
    const minH = minSizeRef.current.h;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    if (g.type === "drag") {
      const left = Math.max(0, Math.min(g.left + dx, vw - g.width));
      const top = Math.max(0, Math.min(g.top + dy, vh - g.height));
      node.style.left = `${left}px`;
      node.style.top = `${top}px`;
      return;
    }

    // resize — the edge/corner opposite the grabbed handle stays anchored
    let { left, top, width, height } = g;
    const rightEdge = g.left + g.width;
    const bottomEdge = g.top + g.height;

    if (g.dir.right) {
      width = Math.min(Math.max(g.width + dx, minW), vw - g.left);
    }
    if (g.dir.left) {
      width = Math.min(Math.max(g.width - dx, minW), rightEdge);
      left = rightEdge - width;
    }
    if (g.dir.bottom) {
      height = Math.min(Math.max(g.height + dy, minH), vh - g.top);
    }
    if (g.dir.top) {
      height = Math.min(Math.max(g.height - dy, minH), bottomEdge);
      top = bottomEdge - height;
    }

    node.style.left = `${left}px`;
    node.style.top = `${top}px`;
    node.style.width = `${width}px`;
    node.style.height = `${height}px`;
  }, []);

  const handleGestureEnd = useCallback(() => {
    interactionRef.current = null;
    document.removeEventListener("mousemove", handleGestureMove);
    document.removeEventListener("mouseup", handleGestureEnd);
    document.body.style.userSelect = "";
  }, [handleGestureMove]);

  const startGesture = useCallback(
    (node, gesture) => {
      const rect = node.getBoundingClientRect();
      interactionRef.current = {
        ...gesture,
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height,
      };
      document.body.style.userSelect = "none";
      document.addEventListener("mousemove", handleGestureMove);
      document.addEventListener("mouseup", handleGestureEnd);
    },
    [handleGestureMove, handleGestureEnd]
  );

  const beginDrag = useCallback(
    (e) => {
      // Ignore drags starting on interactive elements (e.g. the close button)
      if (e.target.closest("button, a, input, textarea, select")) return;
      const node = detachModalCard();
      if (!node) return;
      startGesture(node, { type: "drag", startX: e.clientX, startY: e.clientY });
      e.preventDefault();
    },
    [detachModalCard, startGesture]
  );

  const beginResize = useCallback(
    (dir) => (e) => {
      const node = detachModalCard();
      if (!node) return;
      startGesture(node, {
        type: "resize",
        dir,
        startX: e.clientX,
        startY: e.clientY,
      });
      e.preventDefault();
      e.stopPropagation();
    },
    [detachModalCard, startGesture]
  );

  // Reset all drag/resize state whenever the modal is (re)opened so it always
  // returns to its default centered size and position.
  useEffect(() => {
    if (!isOpen) return undefined;
    isDetachedRef.current = false;
    return () => {
      document.removeEventListener("mousemove", handleGestureMove);
      document.removeEventListener("mouseup", handleGestureEnd);
      document.body.style.userSelect = "";
      const node = modalNodeRef.current;
      if (node) {
        node.style.position = "";
        node.style.margin = "";
        node.style.inset = "";
        node.style.left = "";
        node.style.top = "";
        node.style.width = "";
        node.style.height = "";
        node.style.maxWidth = "";
        node.style.maxHeight = "";
        node.style.transform = "";
        node.style.overflow = "";
      }
      modalNodeRef.current = null;
      isDetachedRef.current = false;
    };
  }, [isOpen, handleGestureMove, handleGestureEnd]);

  // Edge/corner resize handles rendered inside the card.
  const RESIZE_HANDLES = [
    { key: "top", dir: { top: true }, style: { top: 0, left: 0, right: 0, height: 8, cursor: "ns-resize" } },
    { key: "bottom", dir: { bottom: true }, style: { bottom: 0, left: 0, right: 0, height: 8, cursor: "ns-resize" } },
    { key: "left", dir: { left: true }, style: { top: 0, bottom: 0, left: 0, width: 8, cursor: "ew-resize" } },
    { key: "right", dir: { right: true }, style: { top: 0, bottom: 0, right: 0, width: 8, cursor: "ew-resize" } },
    { key: "tl", dir: { top: true, left: true }, style: { top: 0, left: 0, width: 16, height: 16, cursor: "nwse-resize", zIndex: 12 } },
    { key: "tr", dir: { top: true, right: true }, style: { top: 0, right: 0, width: 16, height: 16, cursor: "nesw-resize", zIndex: 12 } },
    { key: "br", dir: { bottom: true, right: true }, style: { bottom: 0, right: 0, width: 16, height: 16, cursor: "nwse-resize", zIndex: 12 } },
    { key: "bl", dir: { bottom: true, left: true }, style: { bottom: 0, left: 0, width: 16, height: 16, cursor: "nesw-resize", zIndex: 12 } },
  ];

  // Helper function to get event type name from ID
  const getEventTypeName = (eventTypeId) => {
    if (!eventTypeId || !eventTypes.length) return "";
    const eventType = eventTypes.find((et) => et.id === eventTypeId);
    return eventType?.name || "";
  };

  // Helper function to check if event type should use RangePicker
  const shouldUseRangePicker = (eventTypeId) => {
    const eventTypeName = getEventTypeName(eventTypeId);
    const lowerName = eventTypeName.toLowerCase().trim();
    if (lowerName === "misc") return false;
    return (
      lowerName === "reunion" ||
      lowerName === "family reunion" ||
      lowerName === "family retreat" ||
      lowerName === "other"
    );
  };

  // Funerals don't ask invitees for an RSVP, so the deadline is optional.
  const isRsvpOptional = (eventTypeId) =>
    getEventTypeName(eventTypeId).toLowerCase().includes("funeral");

  const earliestSelectableEventDayJs = () =>
    dayjs().startOf("day").add(MIN_EVENT_LEAD_DAYS, "day");

  const isRsvpDeadlineValidVsEvent = (rsvpStr, eventStartStr) => {
    if (!rsvpStr?.trim() || !eventStartStr?.trim()) return true;
    const rsvpDay = dayjs(rsvpStr, "YYYY-MM-DD").startOf("day");
    const eventDay = dayjs(eventStartStr, "YYYY-MM-DD").startOf("day");
    if (!rsvpDay.isValid() || !eventDay.isValid()) return false;
    return eventDay.diff(rsvpDay, "day") >= MIN_CALENDAR_DAYS_RSVP_BEFORE_EVENT;
  };

  // Helper function to format person name with suffix or special name (matching TotalFamilyMembers.jsx)
  const formatPersonName = useCallback((member) => {
    // Only use first_name, extract first word from name if first_name not available
    let firstName = member?.first_name || "";
    if (!firstName && member?.name) {
      // Extract first word from full name as fallback
      firstName = member.name.trim().split(' ')[0] || "Unnamed";
    }
    if (!firstName) {
      firstName = "Unnamed";
    }
    
    const suffix = getMemberSuffix(member);
    const specialName = member?.special_name || "";

    // In-laws and UnNamed members always show their last name
    const lastName = String(member?.last_name || "").trim();
    if (lastName && shouldShowDashboardLastName(member, firstName)) {
      return (
        formatDashboardFullPersonName(firstName, lastName, suffix, specialName) ||
        firstName
      );
    }
    
    // Format name with suffix or special name
    if (suffix) {
      return `${firstName}, ${formatSuffix(suffix)}`;
    } else if (specialName) {
      return `${firstName} ${specialName}`;
    }
    return firstName;
  }, []);

  const getHeadOfLineName = useCallback(
    (member) => formatDashboardFamilyLineHeadName(member),
    []
  );

  // Helper function to calculate age from dob
  const getMemberAge = useCallback((member) => {
    // First, check if age is directly provided by the API
    if (member?.age !== undefined && member?.age !== null) {
      const age = parseInt(member.age);
      if (!isNaN(age) && age >= 0) {
        return age;
      }
    }
    
    // Fall back to calculating age from DOB if age is not available
    let dob = member?.dob || member?.date_of_birth || "";
    if (!dob) return -1; // Return -1 for members without DOB
    
    // Handle negative timestamps (dates before 1970) and numeric strings
    if (typeof dob === 'number' || (typeof dob === 'string' && /^-?\d+$/.test(dob))) {
      // Convert timestamp to ISO string
      const timestamp = parseInt(dob);
      dob = new Date(timestamp).toISOString();
    }
    
    const ageStr = calculateAge(dob);
    if (!ageStr) return -1;
    return parseInt(ageStr) || -1;
  }, []);

  const getMemberUserId = (member) =>
    member?.user_id || member?.user?.user_id || member?.user?.id || null;

  const resolveCurrentUserIdentity = useCallback(() => {
    try {
      const localUser = JSON.parse(localStorage.getItem("userDetail") || "{}");
      const role = JSON.parse(
        localStorage.getItem("loggedIn_user_role") || "{}"
      );
      const userId =
        localUser.user_id ||
        localUser.id ||
        role.user_id ||
        role.id ||
        currentUserId ||
        null;
      const email = (localUser.email || role.email || "").trim().toLowerCase();
      const name =
        localUser.name ||
        role.name ||
        [localUser.first_name, localUser.last_name].filter(Boolean).join(" ") ||
        "";
      return { userId, email, name };
    } catch {
      return { userId: currentUserId, email: "", name: "" };
    }
  }, [currentUserId]);

  const memberMatchesUserId = (member, userId) => {
    if (!member || userId == null) return false;
    const uid = getMemberUserId(member);
    if (uid != null && String(uid) === String(userId)) return true;
    const memberId = member.id != null ? String(member.id) : "";
    return memberId !== "" && memberId === String(userId);
  };

  const isSamePersonAsCurrentUser = (member, identity) => {
    if (!member || !identity) return false;
    const { userId, email } = identity;
    if (userId && memberMatchesUserId(member, userId)) return true;
    const memberEmail = (member.email || member.user?.email || "")
      .trim()
      .toLowerCase();
    if (email && memberEmail && memberEmail === email) return true;
    return false;
  };

  const findMemberForUserId = (memberList, userId) => {
    if (!userId || !Array.isArray(memberList)) return null;
    return memberList.find((m) => memberMatchesUserId(m, userId)) || null;
  };

  const findCurrentUserInMembers = (memberList, identity) => {
    if (!Array.isArray(memberList) || !identity) return null;
    if (identity.userId) {
      const byId = findMemberForUserId(memberList, identity.userId);
      if (byId) return byId;
    }
    if (identity.email) {
      const byEmail = memberList.find((m) =>
        isSamePersonAsCurrentUser(m, identity)
      );
      if (byEmail) return byEmail;
    }
    return null;
  };

  const memberMatchesHostValue = (member, hostValue) => {
    if (!member || hostValue == null || hostValue === "") return false;
    const hostId = String(hostValue).trim();
    const memberId = member.id != null ? String(member.id) : "";
    const memberUserId = member.user_id != null ? String(member.user_id) : "";
    return memberId === hostId || memberUserId === hostId;
  };

  // Options for the Host / Co-host dropdowns: adults (plus the logged-in
  // user), alphabetical, shown as "Name • Color Line" in the line colour.
  // `excludeValue` keeps the person picked in one dropdown out of the other.
  const buildHostOptions = (excludeValue) => {
    const identity = resolveCurrentUserIdentity();
    const adultsOnly = members.filter((member) =>
      passesInviteAgeFilter(member, identity)
    );
    const self = resolveSelfMember(members);
    if (
      self &&
      !adultsOnly.some((m) => isSamePersonAsCurrentUser(m, identity))
    ) {
      adultsOnly.push(self);
    }

    const sortedMembers = [...adultsOnly].sort((a, b) =>
      formatPersonName(a)
        .toLowerCase()
        .localeCompare(formatPersonName(b).toLowerCase())
    );

    return sortedMembers
      .filter((member) => !memberMatchesHostValue(member, excludeValue))
      .map((member) => {
        const personName = formatPersonName(member);
        const hexCode = member?.hex_code || "#000000";
        const colorName = member?.color_name ? `${member.color_name} Line` : "";
        return {
          // use id instead of user_id for host
          value: member.id || member.user_id,
          searchLabel: [personName, colorName].filter(Boolean).join(" "),
          label: renderNameWithColor(personName, hexCode, colorName),
        };
      });
  };

  const memberMatchesSearch = (member, lowerCaseSearch) => {
    if (!lowerCaseSearch) return true;
    const label = [
      member?.name,
      member?.first_name,
      member?.email,
      member?.user?.email,
      formatPersonName(member),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return label.includes(lowerCaseSearch);
  };

  const passesInviteAgeFilter = (member, identity) => {
    const age = getMemberAge(member);
    if (age >= 18) return true;
    return isSamePersonAsCurrentUser(member, identity);
  };

  const getAdminDetailsFromStorage = () => {
    try {
      const role = JSON.parse(localStorage.getItem("loggedIn_user_role") || "{}");
      const treeId = family_tree_id || getFamilyTreeIdFromStorage();
      if (treeId && role[treeId]?.admin_details) {
        return role[treeId].admin_details;
      }
      if (role.admin_details) return role.admin_details;
    } catch {
      /* ignore */
    }
    return null;
  };

  const mapHeadOfLineUserToMember = (liu, identity) => {
    if (!liu || typeof liu !== "object") return null;
    const fm = liu.family_member || liu.member || liu;
    const userId =
      fm.user_id ||
      liu.user_id ||
      identity?.userId ||
      null;
    if (!userId && !identity?.email && !identity?.name) return null;

    const displayName =
      fm.name ||
      liu.name ||
      identity?.name ||
      [fm.first_name, fm.last_name].filter(Boolean).join(" ") ||
      "";
    const firstName =
      fm.first_name ||
      liu.first_name ||
      (displayName ? displayName.trim().split(/\s+/)[0] : "") ||
      "You";

    return {
      id:
        fm.id ||
        fm.family_member_id ||
        liu.family_member_id ||
        userId ||
        identity?.email,
      user_id: userId || identity?.userId || identity?.email,
      first_name: firstName,
      name: displayName || firstName,
      email: fm.email || liu.email || identity?.email || "",
      age: fm.age ?? liu.age ?? 18,
      dob: fm.dob || liu.dob || fm.date_of_birth || liu.date_of_birth,
      color_name: fm.color_name || liu.color_name || "",
      hex_code: fm.hex_code || liu.hex_code || "#000000",
      head_first_name: fm.head_first_name || liu.head_first_name || null,
      head_suffix: fm.head_suffix || liu.head_suffix || null,
      head_special_name: fm.head_special_name || liu.head_special_name || null,
      suffix: fm.suffix || liu.suffix || "",
      special_name: fm.special_name || liu.special_name || "",
    };
  };

  const buildCurrentUserMemberFromStorage = useCallback(
    (identityOrUserId) => {
      const identity =
        typeof identityOrUserId === "object" && identityOrUserId !== null
          ? identityOrUserId
          : resolveCurrentUserIdentity();
      const userId = identity.userId || identityOrUserId;
      if (!userId && !identity.email) return null;

      try {
        const localUser = JSON.parse(
          localStorage.getItem("userDetail") || "{}"
        );
        const adminDetails = getAdminDetailsFromStorage();
        const uid = localUser.user_id || localUser.id || userId;
        const displayName =
          adminDetails?.name ||
          identity.name ||
          localUser.name ||
          [localUser.first_name, localUser.last_name]
            .filter(Boolean)
            .join(" ") ||
          "";
        const firstName =
          localUser.first_name ||
          (displayName ? displayName.trim().split(/\s+/)[0] : "") ||
          "You";
        const memberRecordId =
          localUser.member_id ||
          localUser.family_member_id ||
          localUser.family_member?.id ||
          uid;

        return {
          id: memberRecordId,
          user_id: uid,
          first_name: firstName,
          name: displayName || firstName,
          email: identity.email || localUser.email || "",
          age: 18,
          color_name:
            adminDetails?.color_name || localUser.color_name || "",
          hex_code: adminDetails?.hex_code || localUser.hex_code || "#000000",
          head_first_name: adminDetails?.head_first_name || null,
          head_suffix: adminDetails?.head_suffix || null,
          head_special_name: adminDetails?.head_special_name || null,
          suffix: localUser.suffix || "",
          special_name: localUser.special_name || "",
        };
      } catch {
        return null;
      }
    },
    [family_tree_id, resolveCurrentUserIdentity]
  );

  const fetchCurrentUserMemberRecord = useCallback(async () => {
    const identity = resolveCurrentUserIdentity();
    const treeId = family_tree_id || getFamilyTreeIdFromStorage();

    if (treeId) {
      try {
        const response = await getApi(`/head-of-line/${treeId}`);
        const liu = response?.data?.logged_in_user;
        const fromApi = mapHeadOfLineUserToMember(liu, identity);
        if (fromApi) return fromApi;
      } catch (error) {
        console.warn("fetchCurrentUserMemberRecord head-of-line:", error);
      }
    }

    return buildCurrentUserMemberFromStorage(identity);
  }, [
    family_tree_id,
    resolveCurrentUserIdentity,
    buildCurrentUserMemberFromStorage,
  ]);

  const mergeCurrentUserIntoMembers = useCallback(
    (list, selfOverride = null) => {
      if (!Array.isArray(list)) return list;
      const identity = resolveCurrentUserIdentity();
      if (!identity.userId && !identity.email) return list;

      if (findCurrentUserInMembers(list, identity)) return list;

      const selfMember =
        selfOverride ||
        currentUserMember ||
        buildCurrentUserMemberFromStorage(identity);
      if (!selfMember) return list;
      return [...list, selfMember];
    },
    [
      resolveCurrentUserIdentity,
      currentUserMember,
      buildCurrentUserMemberFromStorage,
    ]
  );

  const resolveSelfMember = useCallback(
    (memberList) => {
      const identity = resolveCurrentUserIdentity();
      return (
        findCurrentUserInMembers(memberList, identity) ||
        currentUserMember ||
        buildCurrentUserMemberFromStorage(identity)
      );
    },
    [
      resolveCurrentUserIdentity,
      currentUserMember,
      buildCurrentUserMemberFromStorage,
    ]
  );

  useEffect(() => {
    // Skip during initial load to prevent clearing dates that were just set
    if (isInitialLoad.current) {
      return; // Don't clear the flag here, let the prefill effect handle it
    }

    // CRITICAL: Don't clear dates if we're in edit/view mode and have eventDetails
    // This prevents clearing dates when eventTypes loads
    const isEditOrViewMode = editMode === "Edit" || editMode === "View";
    if (isEditOrViewMode && eventDetails && Object.keys(eventDetails).length > 0) {
      // Don't clear dates during edit/view mode - let the prefill useEffect handle it
      return;
    }

    // When switching event type, reset date fields appropriately
    if (shouldUseRangePicker(form.type)) {
      // Clear single date field when switching TO Family Reunion or Other
      setForm((prev) => ({
        ...prev,
        date: "",
        startDate: "",
        endDate: "",
      }));
      setDateRange([]);
      setSelectingStart(true);
    } else {
      // Clear range fields when switching FROM Family Reunion/Other to single-date events
      setForm((prev) => ({
        ...prev,
        startDate: "",
        endDate: "",
        date: "",
      }));
      setDateRange([]);
      setSelectingStart(true);
    }
  }, [form.type, eventTypes, editMode, eventDetails]);

  // Initialize dateRange from form.startDate and form.endDate when they exist
  // This helps sync dateRange when form dates are set but dateRange isn't
  useEffect(() => {
    if (
      eventTypes.length > 0 &&
      shouldUseRangePicker(form.type) &&
      form.startDate &&
      form.endDate
    ) {
      const start = dayjs(form.startDate, "YYYY-MM-DD");
      const end = dayjs(form.endDate, "YYYY-MM-DD");
      if (start.isValid() && end.isValid()) {
        // Only update if dateRange is empty or different
        if (
          !dateRange ||
          dateRange.length !== 2 ||
          !dateRange[0]?.isSame(start, "day") ||
          !dateRange[1]?.isSame(end, "day")
        ) {
          console.log("Syncing dateRange from form dates:", [start, end]);
          setDateRange([start, end]);
        }
      }
    } else if (eventTypes.length > 0 && !shouldUseRangePicker(form.type)) {
      // Clear dateRange for single date events (but not during initial load if dates exist)
      if (dateRange && dateRange.length > 0 && !isInitialLoad.current) {
        setDateRange([]);
      }
    }
  }, [form.type, form.startDate, form.endDate, eventTypes.length]);

  // Sync form state from dateRange when dateRange has both dates but form doesn't
  // This ensures form state is always in sync with dateRange for range events
  useEffect(() => {
    if (
      eventTypes.length > 0 &&
      shouldUseRangePicker(form.type) &&
      dateRange &&
      dateRange.length === 2 &&
      dateRange[0] &&
      dateRange[1] &&
      dateRange[0].isValid() &&
      dateRange[1].isValid()
    ) {
      const startDateStr = dateRange[0].format("YYYY-MM-DD");
      const endDateStr = dateRange[1].format("YYYY-MM-DD");

      // Only update if form dates are empty or don't match dateRange
      // This prevents unnecessary updates and potential loops
      setForm((prev) => {
        if (prev.startDate === startDateStr && prev.endDate === endDateStr) {
          return prev; // No change needed
        }
        // console.log("Syncing form state from dateRange - startDate:", startDateStr, "endDate:", endDateStr);
        return {
          ...prev,
          startDate: startDateStr,
          endDate: endDateStr,
        };
      });
    }
  }, [dateRange, form.type]);

  useEffect(() => {
    if (isOpen) {
      setMembersFetchComplete(false);
      hasInitializedMembers.current = false;
      formWasResetRef.current = false;
      preResetInvitedMembersRef.current = [];
      setEventAssetsCleared(false);
      if (editMode === "Edit" || editMode === "View") {
        setSelectedMembers([]);
        setInitiallyInvitedMembers([]);
      }
      fetchMemberForInvitation(); //CALL API HERE
    } else {
      setMembersFetchComplete(false);
      hasInitializedMembers.current = false;
      formWasResetRef.current = false;
      preResetInvitedMembersRef.current = [];
      setEventAssetsCleared(false);
      setInitiallyInvitedMembers([]);
    }
  }, [isOpen, eventDetails?.id, editMode]);

  // After family-members API returns, ensure logged-in user is in the list when omitted
  useEffect(() => {
    if (!isOpen || !membersFetchComplete) return;
    const identity = resolveCurrentUserIdentity();
    if (!identity.userId && !identity.email) return;
    setMembers((prev) => mergeCurrentUserIntoMembers(prev));
  }, [
    isOpen,
    membersFetchComplete,
    currentUserId,
    currentUserMember,
    mergeCurrentUserIntoMembers,
    resolveCurrentUserIdentity,
  ]);

  // When adding a new event as a non-creator (User),
  // preselect the current user as Host (and later disable the dropdown)
  useEffect(() => {
    if (
      !isOpen ||
      editMode !== "Add" ||
      !members.length ||
      isCreator
    ) {
      return;
    }

    const identity = resolveCurrentUserIdentity();
    if (!identity.userId && !identity.email) return;

    const selfMember = resolveSelfMember(members);

    if (selfMember && !form.host) {
      const hostValue = selfMember.id || selfMember.user_id;
      if (hostValue) {
        setForm((prev) => ({
          ...prev,
          host: hostValue,
        }));
      }
    }
  }, [
    isOpen,
    editMode,
    members,
    currentUserId,
    isCreator,
    form.host,
    resolveCurrentUserIdentity,
    resolveSelfMember,
  ]);

  // When the logged-in user is the host, they must not appear in invite selections
  useEffect(() => {
    if (!isOpen || !form.host) return;

    const identity = resolveCurrentUserIdentity();
    if (!identity.userId && !identity.email) return;

    const selfMember = resolveSelfMember(members);
    if (!selfMember || !memberMatchesHostValue(selfMember, form.host)) return;

    setSelectedMembers((prev) =>
      prev.filter((m) => !isSamePersonAsCurrentUser(m, identity))
    );
  }, [
    form.host,
    currentUserId,
    members,
    isOpen,
    resolveCurrentUserIdentity,
    resolveSelfMember,
  ]);

  const mapInvitationsToSelectedMembers = useCallback(
    (invitations, memberList) => {
      if (!Array.isArray(invitations) || invitations.length === 0) return [];

      return invitations
        .map((invitedMember) => {
          const userId =
            invitedMember.user_id || invitedMember.user?.user_id || null;
          const memberName =
            invitedMember.user?.name ||
            invitedMember.name ||
            invitedMember.user_name;

          let member = userId
            ? findMemberForUserId(memberList, userId)
            : null;

          if (!member && memberName) {
            member = memberList.find(
              (m) =>
                m.name?.toLowerCase() === memberName.toLowerCase() ||
                (m.user &&
                  m.user.name?.toLowerCase() === memberName.toLowerCase())
            );
          }

          if (member) {
            return {
              ...member,
              user_id: getMemberUserId(member) || userId,
              invitation_id: invitedMember.invitation_id || null,
              note: invitedMember.note || "",
            };
          }

          if (!userId && !memberName) return null;

          return {
            id:
              invitedMember.invitation_id ||
              invitedMember.id ||
              userId ||
              `temp-${Date.now()}`,
            user_id: userId || invitedMember.user_id || invitedMember.id,
            invitation_id: invitedMember.invitation_id || null,
            name: memberName || "Unknown",
            note: invitedMember.note || "",
            image:
              invitedMember.user?.image ||
              invitedMember.image ||
              window.getPublicUrl("/assets/images/profile.svg"),
          };
        })
        .filter((m) => m !== null);
    },
    []
  );

  const isMemberInSelection = (selection, member) => {
    const memberUserId = getMemberUserId(member);
    const memberId = member?.id != null ? String(member.id) : "";
    return selection.some((m) => {
      const selectedUserId = getMemberUserId(m);
      if (memberUserId && selectedUserId && memberUserId === selectedUserId) {
        return true;
      }
      const selectedId = m?.id != null ? String(m.id) : "";
      return memberId && selectedId && memberId === selectedId;
    });
  };

  // Load invited members after family-members API completes (edit/view only)
  useEffect(() => {
    if (formWasResetRef.current) {
      return;
    }
    if (
      (editMode === "Edit" || editMode === "View") &&
      eventDetails &&
      membersFetchComplete &&
      !hasInitializedMembers.current
    ) {
      const invitations =
        eventDetails.Event_Invitations || eventDetails.invited_members || [];
      const mappedMembers = mapInvitationsToSelectedMembers(
        invitations,
        members
      );
      setSelectedMembers(mappedMembers);
      if (editMode === "Edit") {
        setInitiallyInvitedMembers(mappedMembers);
      }
      hasInitializedMembers.current = true;
    }
  }, [
    members,
    editMode,
    eventDetails,
    membersFetchComplete,
    mapInvitationsToSelectedMembers,
  ]);

  const isMemberInitiallyInvited = (member) =>
    editMode === "Edit" &&
    isMemberInSelection(initiallyInvitedMembers, member);

  // Reset the initialization flag when modal closes or mode changes
  useEffect(() => {
    if (!isOpen || editMode === "Add") {
      hasInitializedMembers.current = false;
      isInitialLoad.current = true;
    }
  }, [isOpen, editMode]);

  useEffect(() => {
    const lowerCaseSearch = searchMember.toLowerCase().trim();
    const identity = resolveCurrentUserIdentity();

    let filtered = [];
    if (lowerCaseSearch === "") {
      filtered = [...members];
    } else {
      filtered = members.filter((mem) => memberMatchesSearch(mem, lowerCaseSearch));
    }

    // No age limit on invitees — every living member, in-laws and babies included
    // Family color filter (client-side)
    if (appliedFamilyColor.length > 0) {
      filtered = filtered.filter((member) =>
        appliedFamilyColor.includes(member.hex_code)
      );
    }

    // Gender filter (client-side)
    if (appliedGender === "1" || appliedGender === "2" || appliedGender === "3") {
      filtered = filtered.filter(
        (member) => getMemberGenderCode(member) === appliedGender
      );
    }

    // Selection filter (client-side) — narrow the table to the members already
    // invited (or the ones still to pick) so it can be read against the
    // invited-members list on the left.
    if (appliedSelectionFilter === "selected") {
      filtered = filtered.filter((member) =>
        isMemberInSelection(selectedMembers, member)
      );
    } else if (appliedSelectionFilter === "unselected") {
      filtered = filtered.filter(
        (member) => !isMemberInSelection(selectedMembers, member)
      );
    }

    // Exclude the selected host from the invitee list (host cannot invite themselves)
    if (form.host) {
      const hostId = String(form.host).trim();
      if (hostId) {
        filtered = filtered.filter((member) => {
          const memberId = member.id != null ? String(member.id) : "";
          const memberUserId = member.user_id != null ? String(member.user_id) : "";
          return memberId !== hostId && memberUserId !== hostId;
        });
      }
    }

    // API may omit the logged-in user — add them when not host
    if (identity.userId || identity.email) {
      const selfIsHost =
        form.host &&
        (() => {
          const self = resolveSelfMember(members);
          return self && memberMatchesHostValue(self, form.host);
        })();

      if (!selfIsHost) {
        const self = resolveSelfMember(members);
        if (
          self &&
          memberMatchesSearch(self, lowerCaseSearch) &&
          !filtered.some((m) => isSamePersonAsCurrentUser(m, identity))
        ) {
          filtered = [...filtered, self];
        }
      }
    }

    const localeOpts = { sensitivity: "base", numeric: true };

    const compareByDisplayName = (a, b) => {
      const nameA = formatPersonName(a).toLowerCase();
      const nameB = formatPersonName(b).toLowerCase();
      let comparison = nameA.localeCompare(nameB, undefined, localeOpts);
      if (comparison === 0) {
        comparison = String(a?.id ?? "").localeCompare(
          String(b?.id ?? ""),
          undefined,
          { numeric: true }
        );
      }
      return comparison;
    };

    // Apply sorting
    if (sortBy) {
      filtered = [...filtered].sort((a, b) => {
        let comparison = 0;
        
        if (sortBy === 'name') {
          comparison = compareByDisplayName(a, b);
        } else if (sortBy === 'familyLine') {
          // Sort by head of line name first, then by color name, then by display name
          const headA = getHeadOfLineName(a) || "Start/Top of Line";
          const headB = getHeadOfLineName(b) || "Start/Top of Line";
          const colorA = a?.color_name || "";
          const colorB = b?.color_name || "";
          
          comparison = headA.localeCompare(headB, undefined, localeOpts);
          if (comparison === 0) {
            comparison = colorA.localeCompare(colorB, undefined, localeOpts);
          }
          if (comparison === 0) {
            comparison = compareByDisplayName(a, b);
          }
        }
        
        return sortOrder === 'asc' ? comparison : -comparison;
      });
    }

    setFilteredMembers(filtered);
  }, [
    searchMember,
    members,
    appliedFamilyColor,
    appliedGender,
    appliedSelectionFilter,
    selectedMembers,
    sortBy,
    sortOrder,
    formatPersonName,
    getHeadOfLineName,
    getMemberAge,
    form.host,
    currentUserMember,
    resolveCurrentUserIdentity,
    resolveSelfMember,
  ]);


  // Head-count per applied Line/Color, shown next to the "Invite Members"
  // title. Counts come from the rows currently listed, so they always match
  // what the table shows.
  const appliedFamilyColorCounts = useMemo(() => {
    if (!appliedFamilyColor || appliedFamilyColor.length === 0) return [];

    return appliedFamilyColor.map((hex) => {
      const listEntry = familyColorList.find((c) => c?.hex_code === hex);
      const memberEntry = members.find((m) => m?.hex_code === hex);
      const colorName =
        listEntry?.color_name ||
        listEntry?.name ||
        memberEntry?.color_name ||
        "";

      return {
        hex,
        label: colorName ? `${colorName} Line` : "No Line",
        count: filteredMembers.filter((m) => m?.hex_code === hex).length,
      };
    });
  }, [appliedFamilyColor, familyColorList, members, filteredMembers]);

  // What each filter trigger reads while its filter is applied
  const ageRangeFilterValue = appliedAgeRange
    ? // word joiners keep the range on one line when the trigger wraps
      `${appliedAgeRange.from}\u2060–\u2060${appliedAgeRange.to}`
    : "";
  const familyColorFilterValue = useMemo(() => {
    if (!appliedFamilyColor || appliedFamilyColor.length === 0) return "";
    const names = appliedFamilyColor.map((hex) => {
      const entry = familyColorList.find((c) => c?.hex_code === hex);
      return entry?.color_name || entry?.name || "";
    });
    return (
      formatFilterValueList(names) || `${appliedFamilyColor.length} selected`
    );
  }, [appliedFamilyColor, familyColorList]);
  const genderFilterValue = GENDER_FILTER_LABELS[appliedGender] || "";
  const selectionFilterValue =
    appliedSelectionFilter === "selected"
      ? `Yes (${selectedMembers.length})`
      : appliedSelectionFilter === "unselected"
      ? "No"
      : "";

  const appliedFamilyColorTotal = appliedFamilyColorCounts.reduce(
    (sum, entry) => sum + entry.count,
    0
  );

  // Fetch the color data
  const fetchFamilyColorData = async () => {
    try {
      const family_tree_id_value = getFamilyTreeIdFromStorage() || family_tree_id;
      const res = await getApi(`/family-members-count-by-filter?family_tree_id=${family_tree_id_value}&type=${0}`);

      setFamilyColorList(res?.data?.members || []);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchEventTypes = async () => {
    try {
      const response = await getApi("/event-types");
      let list = Array.isArray(response?.data) ? [...response.data] : [];
      const hasMisc = list.some(
        (t) => String(t?.name || "").trim().toLowerCase() === "misc"
      );
      if (!hasMisc) {
        list.push({ id: MISC_NO_DATE_RANGE_EVENT_TYPE_ID, name: "Misc" });
      }
      setEventTypes(list);
    } catch (error) {
      console.log(error);
    }
  };

  // Fetch members for the invitation
  // let family_tree_id;
  const fetchMemberForInvitation = async (filters = {}) => {
    try {
      const selfRecord = await fetchCurrentUserMemberRecord();
      if (selfRecord) {
        setCurrentUserMember(selfRecord);
        if (selfRecord.user_id) {
          setCurrentUserId((prev) => prev || selfRecord.user_id);
        }
      }

      const params = new URLSearchParams(filters).toString();

      const endpoint = params
        ? `/family-members-for-invitation-with-spouses?family_tree_id=${family_tree_id}&${params}`
        : `/family-members-for-invitation-with-spouses?family_tree_id=${family_tree_id}`;
      const response = await getApi(endpoint);
      const list = response.data?.members || [];
      setMembers(mergeCurrentUserIntoMembers(list, selfRecord));
      setMembersFetchComplete(true);
    } catch (error) {
      console.log("Error from fetch member for invitation", error);
      toast.error(error?.message || "Failed to fetch member for invitation");
      setMembersFetchComplete(true);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: name === "title" ? toEventTitleCase(value) : value,
    }));
  };


  const handleDateChange = (dates) => {
    console.log("handleDateChange called with dates:", dates);

    let startDateStr = "";
    let endDateStr = "";

    if (!dates || dates.length === 0) {
      // Case 1: Dates cleared/removed
      setDateRange([]);
      setSelectingStart(true);
      setForm((prev) => ({
        ...prev,
        startDate: "",
        endDate: "",
      }));

      return;
    }

    setDateRange(dates);

    if (dates[0] && dates[1]) {
      // Both dates are selected (COMPLETE)
      startDateStr = dates[0].format("YYYY-MM-DD");
      endDateStr = dates[1].format("YYYY-MM-DD");
      console.log(
        "Setting dates - startDate:",
        startDateStr,
        "endDate:",
        endDateStr
      );

      // Update form state with the calculated strings
      setForm((prev) => ({
        ...prev,
        startDate: startDateStr,
        endDate: endDateStr,
      }));

      // CLEAR THE ERROR immediately after form state update
      // Use setTimeout to ensure it happens after React processes the state update
    } else if (dates[0]) {
      // Only start date is selected (INCOMPLETE)
      startDateStr = dates[0].format("YYYY-MM-DD");
      endDateStr = "";
      console.log("Setting only startDate:", startDateStr);

      // Update form state
      setForm((prev) => ({
        ...prev,
        startDate: startDateStr,
        endDate: endDateStr,
      }));
    }
  };

  //Handle single date - updated to work with Ant Design DatePicker
  const handleSingleDateChange = (date, dateString) => {
    // date is a dayjs object, dateString is the formatted string based on format prop
    // We need to convert to YYYY-MM-DD format for storage
    let newDate = "";
    if (date && date.isValid()) {
      newDate = date.format("YYYY-MM-DD");
    }

    // 1. Update the form state (keep startDate in sync for API submit on edit)
    setForm((prev) => ({ ...prev, date: newDate, startDate: newDate }));

  };

  //Handle RSVP date change
  const handleRsvpDateChange = (date, dateString) => {
    let newRsvpDate = "";
    if (date && date.isValid()) {
      newRsvpDate = date.format("YYYY-MM-DD");
    }

    if (!newRsvpDate) {
      setForm((prev) => ({ ...prev, rsvp: "" }));
      return;
    }

    const isRangeEvent = shouldUseRangePicker(form.type);
    const eventDateStr = isRangeEvent ? form.startDate : form.date;

    if (eventDateStr && eventDateStr.trim() !== "") {
      if (!isRsvpDeadlineValidVsEvent(newRsvpDate, eventDateStr)) {
        toast.error(
          `RSVP deadline must be at least ${MIN_CALENDAR_DAYS_RSVP_BEFORE_EVENT} full calendar days before the event date.`
        );
        return;
      }
    }

    setForm((prev) => ({ ...prev, rsvp: newRsvpDate }));
  };

  //Handle age from change
  const handleAgeFromChange = (e) => {
    const value = e.target.value;
    setAgeFrom(value);
    
    // If "To" is set and the new "From" is greater than or equal to "To", clear "To"
    if (ageTo && value && !isNaN(parseInt(value))) {
      const fromAge = parseInt(value);
      const toAge = parseInt(ageTo);
      if (!isNaN(toAge) && fromAge >= toAge) {
        setAgeTo("");
      }
    }
  };


  const resetSortToNameAscending = () => {
    setSortBy("name");
    setSortOrder("asc");
  };

  //Handle age to change
  const handleAgeToChange = (e) => {
    const value = e.target.value;
    // Allow free typing - validation will happen on Apply
    setAgeTo(value);
  };

  //Handle apply age range
  const handleApplyAgeRange = () => {
    if (!ageFrom || !ageTo) {
      return toast.info("Please enter both From and To age values");
    }

    const fromAge = parseInt(ageFrom);
    const toAge = parseInt(ageTo);

    if (isNaN(fromAge) || isNaN(toAge)) {
      return toast.info("Please enter valid age numbers");
    }

    if (toAge <= fromAge) {
      return toast.info("To age must be greater than From age");
    }

    // Define the filter object for the API query parameter
    const filterParams = {
      ageFrom: fromAge,
      ageTo: toAge,
    };

    setSearchMember("");

    setAppliedFamilyColor([]);
    setSelectedFamilyColor([]);
    resetSortToNameAscending();

    // Make the API call with the filter
    fetchMemberForInvitation(filterParams);
    setAppliedAgeRange({ from: fromAge, to: toAge });

    setShowAgeRange(false);
    toast.success("Filtered by Age Range");
  };

  //Handle clear age range
  const handleClearAgeRange = () => {
    setAgeFrom("");
    setAgeTo("");
    setAppliedAgeRange(null);
    setSearchMember("");
    setAppliedFamilyColor([]);
    setSelectedFamilyColor([]);
    resetSortToNameAscending();
    fetchMemberForInvitation(); // Reset to all members
    setShowAgeRange(false);
    toast.success("Age range filter cleared");
  };

  //Handle apply color change generation
  const handleApplyFamilyColor = () => {
    if (!selectedFamilyColor || selectedFamilyColor.length === 0) {
      toast.info("Please select a family color");
      return;
    }

    setSearchMember("");
    setAppliedFamilyColor([...selectedFamilyColor]);
    resetSortToNameAscending();
    setShowFamilyColor(false);
    toast.success("Filtered by Family Color");
  };

  const handleClearFamilyColor = () => {
    setSelectedFamilyColor([]);
    setAppliedFamilyColor([]);
    setSearchMember("");
    resetSortToNameAscending();
    // Re-fetches the unfiltered list, which drops any applied age range too
    setAgeFrom("");
    setAgeTo("");
    setAppliedAgeRange(null);
    fetchMemberForInvitation();
    setShowFamilyColor(false);
    toast.success("Family color filter cleared");
  };

  const handleApplyGender = () => {
    if (!selectedGender || !["1", "2", "3"].includes(selectedGender)) {
      toast.info("Please select a gender");
      return;
    }
    setAppliedGender(selectedGender);
    setSearchMember("");
    resetSortToNameAscending();
    setShowGender(false);
    toast.success("Filtered by Gender");
  };

  const handleClearGender = () => {
    setSelectedGender("");
    setAppliedGender("");
    setSearchMember("");
    resetSortToNameAscending();
    setShowGender(false);
    toast.success("Gender filter cleared");
  };

  const handleApplySelectionFilter = () => {
    if (!["selected", "unselected"].includes(selectedSelectionFilter)) {
      toast.info("Please select an option");
      return;
    }
    setAppliedSelectionFilter(selectedSelectionFilter);
    setSearchMember("");
    resetSortToNameAscending();
    setShowSelection(false);
    toast.success(
      selectedSelectionFilter === "selected"
        ? "Showing selected members"
        : "Showing members not selected"
    );
  };

  const handleClearSelectionFilter = () => {
    setSelectedSelectionFilter("");
    setAppliedSelectionFilter("");
    setSearchMember("");
    resetSortToNameAscending();
    setShowSelection(false);
    toast.success("Selection filter cleared");
  };

  const toggleMemberSelection = (member) => {
    if (isMemberInitiallyInvited(member)) return;

    setSelectedMembers((prev) => {
      const memberUserId = member.user_id || member.user?.user_id;
      const memberId = member.id;

      // Check by both id and user_id to handle different member structures
      const exists = prev.some((m) => {
        const mUserId = m.user_id || m.user?.user_id;
        const mId = m.id;

        // Match by user_id (preferred) or by id
        return (
          (memberUserId && mUserId && memberUserId === mUserId) ||
          (memberId && mId && memberId === mId)
        );
      });

      if (exists) {
        // Remove - match by user_id or id
        const filtered = prev.filter((m) => {
          const mUserId = m.user_id || m.user?.user_id;
          const mId = m.id;

          // Keep if NOT matching by user_id or id
          const matchesByUserId =
            memberUserId && mUserId && memberUserId === mUserId;
          const matchesById = memberId && mId && memberId === mId;

          return !(matchesByUserId || matchesById);
        });
        return filtered;
      } else {
        // Add - ensure member has all required fields, especially user_id
        const memberToAdd = {
          ...member,
          // CRITICAL: Preserve user_id - this is required for API
          user_id: memberUserId || member.user_id,
          // Preserve id if it exists
          id: memberId || memberUserId || member.id,
        };

        // Log to verify user_id is present
        // console.log("Adding member with user_id:", memberToAdd.user_id);
        // console.log("Member to add:", memberToAdd);

        const updated = [...prev, memberToAdd];
        return updated;
      }
    });
  };

  const handleCalendarChange = (dates) => {
    setSelectingStart(!dates || dates.length < 2); // true if selecting start date
    setDateRange(dates || []);
  };

  const disabledDate = (current) => {
    if (!current) return false;

    const today = dayjs().startOf("day");
    const selectingStartOrNoEnd =
      selectingStart || !Array.isArray(dateRange) || !dateRange[0];

    if (editMode !== "Add") {
      if (selectingStartOrNoEnd) {
        return current.isBefore(today);
      }
      return current.isBefore(dateRange[0].startOf("day"));
    }

    const earliest = earliestSelectableEventDayJs();

    if (selectingStartOrNoEnd) {
      return current.isBefore(earliest);
    }

    const start = dateRange[0];
    return current.isBefore(start.startOf("day"));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      // Create a preview URL for the image
      const fileReader = new FileReader();
      fileReader.onload = () => {
        setPreviewUrl(fileReader.result);
      };
      fileReader.readAsDataURL(file);

      // Update the form with the file
      setForm((prev) => ({ ...prev, image: file }));
    }
  };

  const handleDocumentFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const lowerName = file.name ? file.name.toLowerCase() : "";
      const isPdf =
        file.type === "application/pdf" || lowerName.endsWith(".pdf");
      const isWord =
        file.type === "application/msword" ||
        file.type ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
        lowerName.endsWith(".doc") ||
        lowerName.endsWith(".docx");

      if (!isPdf && !isWord) {
        toast.error("Only PDF or Word files are accepted.");
        setSelectedDocumentFile(null);
      } else {
        setSelectedDocumentFile(file);
      }
    }
    e.target.value = ""; // Allow re-selecting same file
  };



 

  // Handle column sorting
  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle sort order if clicking the same column
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortBy(column);
      setSortOrder('asc');
    }
  };

 

  //Whenuse apply filter on generation

  const toastValidationErrors = (errors) => {
    const msgs = Object.values(errors).filter(Boolean);
    if (msgs.length === 0) return;
    toast.error(msgs.join(" "));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editMode === "Edit") {
      // Validation for edit mode
      const newErrors = {};
      const isRangeEvent = shouldUseRangePicker(form.type);

      if (!form.title || !form.title.trim()) {
        newErrors.title = "Event Title is required.";
      }
      if (!form.type || form.type.trim() === "" || form.type === "Birthday") {
        newErrors.type = "Event Type is required.";
      }

      // Date/Date Range Validation
      if (isRangeEvent) {
        const hasFormDates =
          form.startDate &&
          form.endDate &&
          form.startDate.trim() !== "" &&
          form.endDate.trim() !== "";
        const hasRangeDates =
          dateRange && dateRange.length === 2 && dateRange[0] && dateRange[1];

        if (!hasFormDates && !hasRangeDates) {
          newErrors.date = "Both Start and End Dates are required.";
        }
      } else if (!form.date || form.date.trim() === "") {
        newErrors.date = "Event Date is required.";
      }

      // Event date must be greater than current date (Edit only)
      const eventDateStrForFuture = isRangeEvent
        ? (form.startDate || "").trim()
        : (form.date || "").trim();
      if (eventDateStrForFuture && !newErrors.date) {
        const today = dayjs().startOf("day");
        const eventDay = dayjs(eventDateStrForFuture, "YYYY-MM-DD").startOf("day");
        if (!eventDay.isAfter(today)) {
          newErrors.date = "Event date must be after the current date.";
        }
      }

      // Time validation
      if (!form.time || !form.time.trim()) {
        newErrors.time = "Time is required.";
      }

      if (!form.rsvp || !form.rsvp.trim()) {
        if (!isRsvpOptional(form.type)) {
          newErrors.rsvp = "RSVP Deadline is required.";
        }
      } else {
        const eventDateStr = isRangeEvent ? form.startDate : form.date;
        if (eventDateStr && eventDateStr.trim() !== "") {
          if (!isRsvpDeadlineValidVsEvent(form.rsvp, eventDateStr)) {
            newErrors.rsvp = `RSVP deadline must be at least ${MIN_CALENDAR_DAYS_RSVP_BEFORE_EVENT} full calendar days before the event date.`;
          }
        }
      }
      if (!form.host || !form.host.trim()) {
        newErrors.host = "Host is required.";
      }
      if (!form.address || !form.address.trim()) {
        newErrors.address = "Address is required.";
      }
      // if (!selectedFile) {
      //   newErrors.banner = "Event Banner/Photo is required."; // We'll use 'banner' as the key
      // }

      if (Object.keys(newErrors).length > 0) {
        toastValidationErrors(newErrors);
        return;
      }

      if(!selectedMembers ||selectedMembers.length === 0) {
        toast.error("Please select member for the invitation");
        return;
      }

      if (!eventDetails || !eventDetails.id) {
        toast.error("Event ID is missing. Cannot update event.");
        return;
      }

      setLoading(true);
      try {
        const family_tree_id_value = getFamilyTreeIdFromStorage() || family_tree_id;
        
        const formData = new FormData();

        formData.append("event_id", eventDetails.id);
        formData.append("event_type_id", form.type);
        formData.append("title", form.title || "");
        formData.append("family_tree_id", family_tree_id_value);
        formData.append("host_id", form.host || "");
        formData.append("co_host_id", form.coHost || "");

        if (shouldUseRangePicker(form.type)) {
          formData.append("start_date", eventDateForApi(form.startDate));
          formData.append("end_date", eventDateForApi(form.endDate));
        } else {
          formData.append("start_date", eventDateForApi(form.date || form.startDate));
        }

        formData.append("event_time", eventTimeForApi(form.time));
        formData.append("venue", form.venue || "");
        // Use address as location (address combines address, city, state)
        formData.append("location", form.address || form.location || "");
        formData.append("state", ""); // State is now part of address field
        formData.append("Rsvp_deadline", eventDateForApi(form.rsvp));
        formData.append("description", form.description || "");

        const priorInvitations = formWasResetRef.current
          ? preResetInvitedMembersRef.current
          : initiallyInvitedMembers
              .map(memberInvitationSnapshot)
              .filter(Boolean);

        appendEditInvitationFields(
          formData,
          selectedMembers,
          priorInvitations,
          formWasResetRef.current
        );

        // Add new image only if updated
        if (selectedFile && selectedFile instanceof File) {
          formData.append("image", selectedFile);
        }
        if (selectedDocumentFile && selectedDocumentFile instanceof File) {
          formData.append("document", selectedDocumentFile);
        }

        // is_edit: 1 only if event details (inputs, photo, document) changed; do not consider invite members
        const initial = initialEditDetailsRef.current;
        let isEdit = 0;
        if (initial) {
          const str = (v) => (v == null ? "" : String(v)).trim();
          const changed =
            str(form.host) !== str(initial.host) ||
            str(form.coHost) !== str(initial.coHost) ||
            str(form.title) !== str(initial.title) ||
            str(form.type) !== str(initial.type) ||
            str(form.venue) !== str(initial.venue) ||
            str(form.address) !== str(initial.address) ||
            str(form.description) !== str(initial.description) ||
            str(form.rsvp) !== str(initial.rsvp) ||
            str(form.time) !== str(initial.time) ||
            str(form.date) !== str(initial.date) ||
            str(form.startDate) !== str(initial.startDate) ||
            str(form.endDate) !== str(initial.endDate) ||
            (selectedFile && selectedFile instanceof File) ||
            (selectedDocumentFile && selectedDocumentFile instanceof File);
          isEdit = changed ? 1 : 0;
        }

        formData.append("is_draft", "0");
        formData.append("is_edit", String(isEdit));
        const response = await putApi(`/events/${eventDetails.id}`, formData);
        // console.log("This is the edit console", response);

        if (response?.statusCode === 200) {
          toast.success("Event updated successfully");
          onClose(true);
        } else {
          toast.error(response?.message || "Failed to update event");
          // Don't close modal if status code is not 200
        }
      } catch (error) {
        console.error("Error updating event:", error);
        toast.error(error?.message || "Failed to update event");
      } finally {
        setLoading(false);
      }

      return;
    } else if (editMode === "Add") {
      const newErrors = {};
      const isRangeEvent = shouldUseRangePicker(form.type);

      // --- VALIDATION LOGIC ---

      if (!form.title.trim()) {
        newErrors.title = "Event Title is required.";
      }
      // Check if type is empty or if it's the default "Birthday" string (not a valid UUID)
      if (!form.type || form.type.trim() === "" || form.type === "Birthday") {
        newErrors.type = "Event Type is required.";
      }

      // Date/Date Range Validation
      if (isRangeEvent) {
        // For range events, check if both startDate and endDate are set
        // Also check dateRange as fallback in case form state hasn't updated yet
        const hasFormDates =
          form.startDate &&
          form.endDate &&
          form.startDate.trim() !== "" &&
          form.endDate.trim() !== "";
        const hasRangeDates =
          dateRange && dateRange.length === 2 && dateRange[0] && dateRange[1];

        if (!hasFormDates && !hasRangeDates) {
          newErrors.date = "Both Start and End Dates are required.";
        }
      } else if (!form.date || form.date.trim() === "") {
        newErrors.date = "Event Date is required.";
      }

      // Event must start at least MIN_EVENT_LEAD_DAYS days from today (new events only)
      const eventStartForLead = isRangeEvent
        ? (form.startDate || "").trim()
        : (form.date || "").trim();
      if (eventStartForLead && !newErrors.date) {
        const leadEarliest = earliestSelectableEventDayJs();
        const startDay = dayjs(eventStartForLead, "YYYY-MM-DD").startOf("day");
        if (startDay.isBefore(leadEarliest)) {
          newErrors.date = `Event date must be at least ${MIN_EVENT_LEAD_DAYS} calendar days from today.`;
        }
      }

      // Time validation
      if (!form.time || !form.time.trim()) {
        newErrors.time = "Time is required.";
      }

      if (!form.rsvp || !form.rsvp.trim()) {
        if (!isRsvpOptional(form.type)) {
          newErrors.rsvp = "RSVP Deadline is required.";
        }
      } else {
        const eventDateStr = isRangeEvent ? form.startDate : form.date;
        if (eventDateStr && eventDateStr.trim() !== "") {
          if (!isRsvpDeadlineValidVsEvent(form.rsvp, eventDateStr)) {
            newErrors.rsvp = `RSVP deadline must be at least ${MIN_CALENDAR_DAYS_RSVP_BEFORE_EVENT} full calendar days before the event date.`;
          }
        }
      }
      if (!form.host || !form.host.trim()) {
        newErrors.host = "Host is required.";
      }
      if (!form.address || !form.address.trim()) {
        newErrors.address = "Address is required.";
      }
      if (!selectedFile) {
        newErrors.banner = "Event Banner/Photo is required."; // We'll use 'banner' as the key
      }

      // --- END VALIDATION LOGIC ---

      if (Object.keys(newErrors).length > 0) {
        toastValidationErrors(newErrors);
        return; // STOP API CALL
      }

      // Check if members are selected for invitation
      if (!selectedMembers || selectedMembers.length === 0) {
        toast.error("Please select member for the invitation");
        return; // STOP API CALL
      }

      console.log("Creating new event:", form);

      setLoading(true);
      try {
        const formData = new FormData();
        const family_tree_id_value = getFamilyTreeIdFromStorage() || family_tree_id;

        // Add all form fields to formData
        // formData.append('event_type_id', "44965346-f88a-463b-ad63-161380a4e09c");
        formData.append("event_type_id", form.type);
        formData.append("title", form.title || "");
        formData.append("family_tree_id", family_tree_id_value);
        formData.append("host_id", form.host || "");
        formData.append("co_host_id", form.coHost || "");

        // Handle date fields:
        // - For Reunion/Other events: send both start_date and end_date
        // - For other event types: send only start_date (no end_date)
        if (shouldUseRangePicker(form.type)) {
          // For Family Reunion/Other events, use startDate and endDate
          // Use dateRange as fallback if form state hasn't updated yet
          let startDate = form.startDate?.trim() || "";
          let endDate = form.endDate?.trim() || "";

          // If form dates are empty but dateRange has values, use dateRange
          if (
            (!startDate || !endDate) &&
            dateRange &&
            dateRange.length === 2 &&
            dateRange[0] &&
            dateRange[1]
          ) {
            startDate = dateRange[0].format("YYYY-MM-DD");
            endDate = dateRange[1].format("YYYY-MM-DD");
            console.log(
              "Using dateRange fallback - startDate:",
              startDate,
              "endDate:",
              endDate
            );
          }

          console.log(
            "Submitting dates - startDate:",
            startDate,
            "endDate:",
            endDate,
            "form.startDate:",
            form.startDate,
            "form.endDate:",
            form.endDate,
            "dateRange:",
            dateRange
          );

          if (!startDate || !endDate) {
            console.error(
              "Warning: startDate or endDate is empty when submitting range event!"
            );
          }

          const startUTC = eventDateForApi(startDate);
          const endUTC = eventDateForApi(endDate);
          formData.append("start_date", startUTC);
          formData.append("end_date", endUTC);
        } else {
          // For single-date events, use date as start_date only (no end_date)
          formData.append("start_date", eventDateForApi(form.date));
        }

        formData.append("event_time", eventTimeForApi(form.time));
        formData.append("venue", form.venue || "");
        // Use address as location (address combines address, city, state)
        formData.append("location", form.address || form.location || "");
        formData.append("state", ""); // State is now part of address field
        formData.append("Rsvp_deadline", eventDateForApi(form.rsvp));
        formData.append("description", form.description || "");

        // Add invited_members as JSON string array
        // Use user_id (not id) as id is the family member ID, not the user ID
        const membersWithUserId = selectedMembers.filter(
          (member) => member.user_id
        );
        const membersWithoutUserId = selectedMembers.filter(
          (member) => !member.user_id
        );

        if (membersWithoutUserId.length > 0) {
          warnMembersWithoutAccount(membersWithoutUserId);
        }

        const invitedMembersArray = membersWithUserId.map((member) => ({
          user_id: member.user_id,
          note: member.note || "",
        }));

        // console.log("Invited members array:", invitedMembersArray);
        formData.append("invited_members", JSON.stringify(invitedMembersArray));

        // Add the image file if it exists
        if (selectedFile && selectedFile instanceof File) {
          formData.append("image", selectedFile);
        }
        if (selectedDocumentFile && selectedDocumentFile instanceof File) {
          formData.append("document", selectedDocumentFile);
        }

        formData.append("is_draft", "0");
        const response = await postApi("/events", formData);
        // console.log("response from events post api : ", response);

        if (response?.statusCode === 200) {
          toast.success("Event created successfully");
          // Close modal and trigger refresh
          onClose(true);
        } else {
          toast.error(response?.message || "Failed to create event");
          // Don't close modal if status code is not 200
        }
        console.log(response);
      } catch (error) {
        console.log(error);
        toast.error(error?.message || "Failed to create event");
        // Don't close modal on error
      } finally {
        setLoading(false);
      }
    } else {
      // For Edit mode, just close without refresh for now
      onClose(false);
    }
  };

  const handleSaveDraft = async (e) => {
    e.preventDefault();
    if (editMode === "View") return;

    const isDraft = true;
    const newErrors = {};
    if (!form.title || !form.title.trim()) {
      newErrors.title = "Event Title is required.";
    }
    if (Object.keys(newErrors).length > 0) {
      toastValidationErrors(newErrors);
      return;
    }

    setDraftLoading(true);
    try {
      const family_tree_id_value = getFamilyTreeIdFromStorage() || family_tree_id;

      const formData = new FormData();
      formData.append("event_type_id", form.type || "");
      formData.append("title", form.title || "");
      formData.append("family_tree_id", family_tree_id_value);
      formData.append("host_id", form.host || "");
      formData.append("co_host_id", form.coHost || "");
      formData.append("is_draft", "1");

      const isRangeEvent = shouldUseRangePicker(form.type);
      if (isRangeEvent && (form.startDate || form.endDate)) {
        formData.append("start_date", eventDateForApi(form.startDate));
        formData.append("end_date", eventDateForApi(form.endDate));
      } else if (form.date) {
        formData.append("start_date", eventDateForApi(form.date));
      } else {
        formData.append("start_date", "");
      }

      if (form.time) {
        formData.append("event_time", eventTimeForApi(form.time));
      } else {
        formData.append("event_time", "");
      }
      formData.append("venue", form.venue || "");
      formData.append("location", form.address || form.location || "");
      formData.append("state", form.state || "");
      formData.append("Rsvp_deadline", form.rsvp ? eventDateForApi(form.rsvp) : "");
      formData.append("description", form.description || "");

      const priorInvitations = formWasResetRef.current
        ? preResetInvitedMembersRef.current
        : initiallyInvitedMembers
            .map(memberInvitationSnapshot)
            .filter(Boolean);

      appendEditInvitationFields(
        formData,
        selectedMembers,
        priorInvitations,
        formWasResetRef.current
      );

      if (selectedFile && selectedFile instanceof File) {
        formData.append("image", selectedFile);
      }
      if (selectedDocumentFile && selectedDocumentFile instanceof File) {
        formData.append("document", selectedDocumentFile);
      }

      if (editMode === "Edit" && eventDetails?.id) {
        formData.append("event_id", eventDetails.id);
        const response = await putApi(`/events/${eventDetails.id}`, formData);
        if (response?.statusCode === 200) {
          toast.success("Event saved as draft");
          onClose(true);
        } else {
          toast.error(response?.message || "Failed to save draft");
        }
      } else {
        const response = await postApi("/events", formData);
        if (response?.statusCode === 200) {
          toast.success("Event saved as draft");
          onClose(true);
        } else {
          toast.error(response?.message || "Failed to save draft");
        }
      }
    } catch (error) {
      console.error("Error saving draft:", error);
      toast.error(error?.message || "Failed to save draft");
    } finally {
      setDraftLoading(false);
    }
  };

  const isCancelledEvent =
    editMode === "Edit" &&
    (eventDetails?.status === 2 || eventDetails?.status === "2");

  const handleResetCancelledForm = () => {
    const invitations =
      eventDetails?.Event_Invitations || eventDetails?.invited_members || [];
    const snapshotFromEvent = invitations
      .map(normalizeInvitationRecord)
      .filter(Boolean);
    const snapshotFromSelection = initiallyInvitedMembers
      .map(memberInvitationSnapshot)
      .filter(Boolean);

    preResetInvitedMembersRef.current =
      snapshotFromEvent.length > 0 ? snapshotFromEvent : snapshotFromSelection;

    formWasResetRef.current = true;
    isInitialLoad.current = true;
    setEventAssetsCleared(true);

    const emptyForm = {
      host: "",
      coHost: "",
      title: "",
      type: "",
      venue: "",
      date: "",
      startDate: "",
      endDate: "",
      time: "",
      address: "",
      location: "",
      state: "",
      rsvp: "",
      description: "",
      banner: "",
      event_type_name: "",
    };

    setForm(emptyForm);
    setPreviewUrl("");
    setSelectedFile(null);
    setSelectedDocumentFile(null);
    setDateRange([]);
    setSelectingStart(true);
    setSelectedMembers([]);
    setInitiallyInvitedMembers([]);
    hasInitializedMembers.current = true;

    setSearchMember("");
    setAgeFrom("");
    setAgeTo("");
    setAppliedAgeRange(null);
    setSelectedFamilyColor([]);
    setAppliedFamilyColor([]);
    setSelectedGender("");
    setAppliedGender("");
    setSelectedSelectionFilter("");
    setAppliedSelectionFilter("");
    setShowAgeRange(false);
    setShowFamilyColor(false);
    setShowGender(false);
    setShowSelection(false);

    initialEditDetailsRef.current = {
      host: "",
      coHost: "",
      title: "",
      type: "",
      venue: "",
      address: "",
      description: "",
      rsvp: "",
      time: "",
      date: "",
      startDate: "",
      endDate: "",
      hasImage: false,
      hasDocument: false,
    };

    if (!isCreator && members.length) {
      const selfMember = resolveSelfMember(members);
      if (selfMember) {
        const hostValue = selfMember.id || selfMember.user_id;
        if (hostValue) {
          setForm((prev) => ({
            ...prev,
            host: hostValue,
          }));
          initialEditDetailsRef.current.host = String(hostValue);
        }
      }
    }

    setTimeout(() => {
      isInitialLoad.current = false;
    }, 100);

    toast.success("Form reset successfully.");
  };

  const isView = editMode === "View";
  const showExistingEventDocument =
    editMode === "Edit" &&
    eventDetails?.document &&
    !selectedDocumentFile &&
    !eventAssetsCleared;

  // Invited-members summary. Desktop keeps it in the left column under the
  // uploads; on mobile it renders below the event input fields instead.
  const invitedMembersSection = (
    <>
          <div className="invite-member-div">
            <h6 className="heading">Invited Family Members ({selectedMembers.length})</h6>

            {/* Show the selected member */}
            <div className="invite-member-list" style={{
              // Two aligned columns: name | dot + line color.
              // Column 1 sizes to the longest name so all dots/lines line up.
              display: "grid",
              gridTemplateColumns: "max-content 1fr",
              columnGap: "1.25em",
              rowGap: "0px",
              alignContent: "start",
              // Everything stacks vertically and scrolls.
              maxHeight: "420px",
              overflowY: "auto",
              overflowX: "hidden",
            }}>
              {selectedMembers.length === 0 ? (
                <p style={{ gridColumn: "1 / -1" }}>No members selected</p>
              ) : (() => {
                const localeOpts = { sensitivity: "base", numeric: true };
                const byName = (a, b) =>
                  formatPersonName(a)
                    .toLowerCase()
                    .localeCompare(
                      formatPersonName(b).toLowerCase(),
                      undefined,
                      localeOpts
                    );

                // More than two invitees: state each line color once as a
                // heading, with its members listed alphabetically beneath it.
                if (selectedMembers.length > 2) {
                  const groups = [];
                  [...selectedMembers].sort(byName).forEach((mem) => {
                    const line = mem?.color_name ? `${mem.color_name} Line` : "No Line";
                    let group = groups.find((g) => g.line === line);
                    if (!group) {
                      group = {
                        line,
                        hex: mem?.hex_code || "#000000",
                        members: [],
                      };
                      groups.push(group);
                    }
                    group.members.push(mem);
                  });
                  groups.sort((a, b) =>
                    a.line.localeCompare(b.line, undefined, localeOpts)
                  );
                  return groups.map((group) => (
                    <div
                      key={group.line}
                      style={{ gridColumn: "1 / -1", marginBottom: "10px" }}
                    >
                      {/* Line heading: color dot + line name, noted once */}
                      <div
                        style={{
                          fontSize: "14px",
                          lineHeight: "1",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <span
                          style={{
                            display: "inline-block",
                            width: "7px",
                            height: "7px",
                            flexShrink: 0,
                            borderRadius: "50%",
                            backgroundColor: group.hex,
                          }}
                        />
                        <span style={{ color: group.hex, fontWeight: 600 }}>
                          {group.line}
                        </span>
                      </div>
                      {/* Members of this line — always black, indented */}
                      {group.members.map((mem) => (
                        <div
                          key={mem.id || mem.user_id}
                          style={{
                            fontSize: "14px",
                            lineHeight: "1",
                            color: "#000000",
                            whiteSpace: "nowrap",
                            paddingLeft: "15px",
                          }}
                        >
                          {formatPersonName(mem)}
                        </div>
                      ))}
                    </div>
                  ));
                }

                return [...selectedMembers]
                  // Group members by color, then alphabetize within each group.
                  .sort((a, b) => {
                    let comparison = (a?.color_name || "").localeCompare(
                      b?.color_name || "",
                      undefined,
                      localeOpts
                    );
                    if (comparison === 0) {
                      comparison = byName(a, b);
                    }
                    return comparison;
                  })
                  .map((mem) => {
                    const hex = mem?.hex_code || "#000000";
                    const line = mem?.color_name ? `${mem.color_name} Line` : "";
                    return (
                      <React.Fragment key={mem.id || mem.user_id}>
                        {/* Column 1: name — always black; the line color reads from column 2 */}
                        <span
                          style={{
                            fontSize: "14px",
                            lineHeight: "1",
                            color: "#000000",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {formatPersonName(mem)}
                        </span>
                        {/* Column 2: color dot + line name */}
                        <span
                          style={{
                            fontSize: "14px",
                            lineHeight: "1",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {line && (
                            <>
                              <span
                                style={{
                                  display: "inline-block",
                                  width: "7px",
                                  height: "7px",
                                  flexShrink: 0,
                                  borderRadius: "50%",
                                  backgroundColor: hex,
                                }}
                              />
                              <span style={{ color: hex }}>{line}</span>
                            </>
                          )}
                        </span>
                      </React.Fragment>
                    );
                  });
              })()}
            </div>
          </div>
          <p className="invite-email-note" style={{
            marginTop: "12px",
            fontSize: "12px",
            color: "#666",
            lineHeight: "1.4"
          }}>
            Please review your email address and let the admin know if it should be changed/updated.
          </p>
    </>
  );

  const rangeDisplay =
    dateRange &&
    dateRange.length === 2 &&
    dateRange[0] &&
    dateRange[1] &&
    dayjs(dateRange[0]).isValid() &&
    dayjs(dateRange[1]).isValid()
      ? `${dayjs(dateRange[0]).format("M/D/YYYY")} - ${dayjs(dateRange[1]).format("M/D/YYYY")}`
      : "";

  return (
    <>
      <button className="event-modal-close" onClick={onClose}>
        ×
      </button>

      {RESIZE_HANDLES.map((h) => (
        <div
          key={h.key}
          onMouseDown={beginResize(h.dir)}
          style={{ position: "absolute", zIndex: 11, ...h.style }}
        />
      ))}

      <div
        ref={modalHeaderRef}
        className="event-modal-drag-handle"
        onMouseDown={beginDrag}
        style={{ cursor: "move", touchAction: "none" }}
        title="Drag to move"
      >
        <h2 className="event-modal-title">
          {editMode === "Add"
            ? "Create New Event"
            : editMode === "Edit"
            ? "Edit Event"
            : "View Event"}
        </h2>
        <p className="event-modal-desc">
          {editMode === "Add"
            ? "Fill out the details below to create a new event."
            : editMode === "Edit"
            ? "Update the details of your event below."
            : "Viewing event details"}
        </p>
      </div>

      <form className="event-form" onSubmit={handleSubmit}>
        <div className="event-form-grid">
          {/* Banner Placeholder */}
          <div className="event-left-flex">
          <h6 className="heading required-label">Event Photo / Image (JPG, PNG, SVG)</h6>

            <>
              <input
                type="file"
                id="event-banner-upload"
                accept="image/jpeg,image/jpg,image/png,image/svg+xml"
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
              <div
                className="event-banner-placeholder event-banner-placeholder--photo"
                onClick={() =>
                  document.getElementById("event-banner-upload").click()
                }
                onMouseEnter={() => setIsHoveringBanner(true)}
                onMouseLeave={() => setIsHoveringBanner(false)}
                style={
                  previewUrl
                    ? {
                        backgroundImage: `url(${previewUrl})`,
                        backgroundSize: "contain",
                        backgroundPosition: "center",
                        backgroundRepeat: "no-repeat",
                        minHeight: "180px",
                        maxHeight: "180px",
                        position: "relative",
                      }
                    : {
                        minHeight: "180px",
                        maxHeight: "180px",
                        position: "relative",
                      }
                }
              >
                {!previewUrl && <span className="event-banner-plus">+</span>}
                {previewUrl && isHoveringBanner && (
                  <span
                    className="event-banner-hover-overlay"
                    style={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "rgba(0, 0, 0, 0.5)",
                      color: "#fff",
                      fontSize: "14px",
                      fontWeight: "500",
                      textAlign: "center",
                      padding: "12px",
                      cursor: "pointer",
                    }}
                  >
                    Click to upload different image
                  </span>
                )}
              </div>
            </>
            {/* Event Documents section - PDF and Word */}
            <h6 className="heading" style={{ marginTop: "16px" }}>Event Documents (PDF, Word)</h6>
            <input
              type="file"
              id="event-document-upload"
              accept="application/pdf,.pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.doc,.docx"
              style={{ display: "none" }}
              onChange={handleDocumentFileChange}
            />
            <div
              className="event-banner-placeholder event-banner-placeholder--document"
              onClick={() => {
                if (!showExistingEventDocument) {
                  document.getElementById("event-document-upload").click();
                }
              }}
              style={{
                minHeight: "56px",
                maxHeight: "56px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                cursor: showExistingEventDocument ? "default" : "pointer",
              }}
            >
              {selectedDocumentFile ? (
                <span style={{ fontSize: "14px", color: "#333", textAlign: "center", padding: "0 8px" }}>
                  {selectedDocumentFile.name}
                </span>
              ) : showExistingEventDocument ? (
                <a
                  href={eventDetails.document}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  style={{ color: "#1890ff", textDecoration: "underline", fontSize: "14px" }}
                >
                  {(() => {
                    const raw = eventDetails.document.split("/").pop() || "";
                    const filename = decodeURIComponent(raw);
                    const ext = (filename.split(".").pop() || "file").toUpperCase();
                    return `File uploaded: ${ext}`;
                  })()}
                </a>
              ) : (
                <span className="event-banner-plus">+ Add document</span>
              )}
            </div>
            {showExistingEventDocument && (
              <div style={{ marginTop: "6px", textAlign: "center" }}>
                <button
                  type="button"
                  onClick={() => document.getElementById("event-document-upload").click()}
                  style={{
                    fontSize: "12px",
                    color: "#666",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: 0,
                    textDecoration: "underline",
                  }}
                >
                  Click to replace
                </button>
              </div>
            )}
            {selectedDocumentFile && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setSelectedDocumentFile(null); }}
                style={{
                  marginTop: "6px",
                  fontSize: "12px",
                  color: "#666",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: 0,
                  textDecoration: "underline",
                }}
              >
                Remove document
              </button>
            )}
            {/* <div className="event-banner-placeholder" onClick={handleFileChange}>
              <span className="event-banner-plus">+</span>
            </div> */}
            {!isMobileView && invitedMembersSection}
          </div>
          {/* Fields */}
          <div className="event-form-fields event-form-fields--compact">
            {/* Row 1: Host, Co-host, Event Title, Event Type */}
            <div className="event-field-row">
              <div className="event-field" style={{ flex: "0 0 21%", maxWidth: "21%" }}>
                <label className="required-label">Host</label>
                <DropdownSelect
                  options={buildHostOptions(form.coHost)}
                  value={form.host}
                  onChange={(value) => {
                    setForm((prev) => ({
                      ...prev,
                      host: value,
                      // The same person cannot be both host and co-host
                      coHost:
                        value && String(prev.coHost) === String(value)
                          ? ""
                          : prev.coHost,
                    }));
                    const identity = resolveCurrentUserIdentity();
                    if (value && (identity.userId || identity.email)) {
                      const selfMember = resolveSelfMember(members);
                      if (
                        selfMember &&
                        memberMatchesHostValue(selfMember, value)
                      ) {
                        setSelectedMembers((prev) =>
                          prev.filter(
                            (m) => !isSamePersonAsCurrentUser(m, identity)
                          )
                        );
                      }
                    }
                  }}
                  placeholder="Select Host"
                  disabled={isView || (!isCreator && editMode === "Add")}
                  className="event-dropdown"
                />
              </div>

              <div className="event-field" style={{ flex: "0 0 21%", maxWidth: "21%" }}>
                <label>Co-host</label>
                <DropdownSelect
                  options={buildHostOptions(form.host)}
                  value={form.coHost}
                  onChange={(value) => {
                    setForm((prev) => ({ ...prev, coHost: value }));
                  }}
                  placeholder="Select Co-host"
                  disabled={isView}
                  className="event-dropdown"
                />
              </div>

              <div className="event-field" style={{ flex: "1 1 auto" }}>
                <label className="required-label">Event Title</label>
                <input
                  type="text"
                  name="title"
                  value={form.title}
                  onChange={handleChange}
                  disabled={isView}
                  autoCapitalize="words"
                  placeholder="Smith Family BBQ Party"
                  style={{ height: "36px" }}
                />
              </div>

              <div className="event-field">
                <label className="required-label">Event Type</label>
                <DropdownSelect
                  options={(() => {
                    // Sort event types alphabetically by name, but always put "Other" at the end
                    const sortedEventTypes = [...eventTypes].sort((a, b) => {
                      const nameA = a.name.toLowerCase();
                      const nameB = b.name.toLowerCase();

                      const rank = (n) =>
                        n === "misc" ? 2 : n === "other" ? 1 : 0;
                      const ra = rank(nameA);
                      const rb = rank(nameB);
                      if (ra !== rb) return ra - rb;

                      return nameA.localeCompare(nameB);
                    });
                    // Convert to DropdownSelect format and handle "Family Reunion" -> "Reunion"
                    return sortedEventTypes.map((eventType) => {
                      const lowerName = eventType.name.toLowerCase();
                      let displayName =
                        lowerName === "family reunion" ? "Reunion" : eventType.name;

                      if (lowerName === "misc") {
                        displayName = "Misc (no date range)";
                      } else if (
                        lowerName === "reunion" ||
                        lowerName === "family reunion" ||
                        lowerName === "family retreat" ||
                        lowerName === "other"
                      ) {
                        displayName = `${displayName} (date range)`;
                      }

                      return {
                        value: eventType.id,
                        label: displayName,
                      };
                    });
                  })()}
                  value={form.type}
                  onChange={(value) => {
                    setForm((prev) => ({ ...prev, type: value }));
                  }}
                  placeholder="Select Event Type"
                  disabled={isView}
                  className="event-dropdown"
                />

              </div>
            </div>

            {/* Row 2: Date, Time, Venue */}
            <div className="event-field-row">
              <div className="event-field event-date-field" style={{ flex: "0 0 19%", maxWidth: "19%" }}>
              <label className="required-label">Event Date</label>

                {shouldUseRangePicker(form.type) ? (
                  <div className="event-range-picker-wrapper">
                    <RangePicker
                      className="event-range-picker"
                      value={
                        dateRange &&
                        dateRange.length === 2 &&
                        dateRange[0] &&
                        dateRange[1]
                          ? dateRange
                          : null
                      }
                      onCalendarChange={handleCalendarChange}
                      onChange={handleDateChange}
                      disabledDate={disabledDate}
                      getPopupContainer={(triggerNode) => triggerNode.parentNode}
                      separator="-"
                      allowClear={true}
                      format="M/D/YYYY"
                      placeholder={["Start date", "End date"]}
                      style={{
                        width: "100%",
                        border: "1px solid #D9D9D9",
                        borderRadius: "8px",
                        padding: "6px 12px",
                        height: "36px",
                        fontSize: "12px",
                      }}
                    />
                    <div className="event-range-picker-display" aria-hidden="true">
                      {rangeDisplay || "Start date - End date"}
                    </div>
                  </div>
                ) : (
                  <DatePicker
                    value={form.date ? dayjs(form.date, "YYYY-MM-DD") : null}
                    onChange={handleSingleDateChange}
                    format="MM/DD/YYYY"
                    placeholder="Select date"
                    getPopupContainer={(triggerNode) => triggerNode.parentNode}
                    style={{
                      width: "100%",
                      border: "1px solid #D9D9D9",
                      borderRadius: "8px",
                      padding: "6px 12px",
                      height: "36px",
                      fontSize: "12px",
                    }}
                  disabledDate={(current) => {
                    if (!current) return false;
                    if (editMode !== "Add") {
                      return current.isBefore(dayjs().startOf("day"));
                    }
                    return current.isBefore(earliestSelectableEventDayJs());
                  }}
                  />
                )}
              </div>

              <div className="event-field" style={{ flex: "0 0 15%", maxWidth: "15%" }}>
                <label className="required-label">Time</label>
                <input
                  type="time"
                  name="time"
                  value={form.time}
                  disabled={isView}
                  onChange={handleChange}
                  style={{ height: "36px", width: "100%" }}
                />
              </div>

              <div className="event-field" style={{ flex: "1 1 auto", minWidth: "36%" }}>
                <label className="required-label">Venue</label>
                <input
                  type="text"
                  name="venue"
                  value={form.venue}
                  onChange={handleChange}
                  disabled={isView}
                  placeholder="Enter venue name"
                  style={{ height: "36px" }}
                />
              </div>
            </div>

            {/* Row 3: Address & RSVP Deadline */}
            <div className="event-field-row">
              <div className="event-field" style={{ flex: "1 1 auto", minWidth: 0 }}>
                <label className="required-label">Address</label>
                <input
                  type="text"
                  name="address"
                  value={form.address}
                  disabled={isView}
                  onChange={(e) => {
                    const addressValue = e.target.value;
                    setForm((prev) => ({
                      ...prev,
                      address: addressValue,
                      location: addressValue,
                      state: "",
                    }));
                  }}
                  placeholder="123 Main St, New York, NY"
                  style={{ height: "36px", width: "100%" }}
                />
              </div>

              <div
                className="event-field rsvp-deadline-field"
                style={{ flex: "0 0 148px", maxWidth: "160px", minWidth: "132px" }}
              >
                <label className={isRsvpOptional(form.type) ? undefined : "required-label"}>RSVP Deadline</label>
                <DatePicker
                  value={form.rsvp ? dayjs(form.rsvp, "YYYY-MM-DD") : null}
                  onChange={handleRsvpDateChange}
                  format="MM/DD/YYYY"
                  placeholder="Select date"
                  getPopupContainer={(triggerNode) => triggerNode.parentNode}
                  popupClassName="rsvp-date-picker-popup"
                  popupStyle={{ width: "275px" }}
                  style={{
                    width: "100%",
                    border: "1px solid #D9D9D9",
                    borderRadius: "8px",
                    padding: "6px 12px",
                    height: "36px",
                    fontSize: "12px",
                  }}
                  disabledDate={(current) => {
                    if (!current) return false;

                    const today = dayjs().startOf("day");
                    if (current.isBefore(today)) return true;

                    const isRangeEvent = shouldUseRangePicker(form.type);
                    const eventDateStr = isRangeEvent ? form.startDate : form.date;

                    if (!eventDateStr || eventDateStr.trim() === "") return false;

                    const eventDay = dayjs(eventDateStr, "YYYY-MM-DD").startOf("day");
                    const latestRsvpDay = eventDay.subtract(
                      MIN_CALENDAR_DAYS_RSVP_BEFORE_EVENT,
                      "day"
                    );

                    if (!current.isBefore(eventDay)) return true;
                    if (current.isAfter(latestRsvpDay)) return true;

                    return false;
                  }}
                  disabled={isView}
                />
                <style>{`
                  .ant-picker-input input {
                    font-size: 12px !important;
                  }
                `}</style>
              </div>
            </div>

            {/* Row 4: Special Instructions (full width) */}
            <div className="event-field-row">
              <div className="event-field" style={{ flex: "1", minWidth: 0 }}>
                <label>Special Instructions</label>
                <textarea
                  name="description"
                  className="special-instructions-textarea"
                  value={form.description}
                  onChange={handleChange}
                  disabled={isView}
                  placeholder="Enter any special instructions here"
                  rows={4}
                  style={{ width: "100%" }}
                />
              </div>
            </div>

            {isMobileView && invitedMembersSection}

            <div
              className="popup-header invite-header"
              style={{ marginTop: "16px", flexWrap: "wrap", rowGap: "6px" }}
            >
              <h2>Invite Members</h2>

              {/* Head-count for each Line/Color the filter is set to */}
              {appliedFamilyColorCounts.length > 0 && (
                <div
                  className="invite-line-counts"
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: "10px",
                    marginLeft: "12px",
                    // header is space-between; keep the counts beside the title
                    marginRight: "auto",
                    fontSize: "14px",
                  }}
                >
                  {appliedFamilyColorCounts.map((entry) => (
                    <span
                      key={entry.hex}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "5px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <span
                        style={{
                          display: "inline-block",
                          width: "8px",
                          height: "8px",
                          flexShrink: 0,
                          borderRadius: "50%",
                          backgroundColor: entry.hex || "#000000",
                        }}
                      />
                      <span style={{ color: entry.hex || "#000000", fontWeight: 600 }}>
                        {entry.label}
                      </span>
                      <span style={{ color: "#000000", fontWeight: 600 }}>
                        ({entry.count})
                      </span>
                    </span>
                  ))}
                  {appliedFamilyColorCounts.length > 1 && (
                    <span style={{ color: "#000000", fontWeight: 600, whiteSpace: "nowrap" }}>
                      Total ({appliedFamilyColorTotal})
                    </span>
                  )}
                </div>
              )}

              {/* <button className="button" onClick={onClose} aria-label="Close">
                &times;
              </button> */}
            </div>

            <div className="invite-grid">
              <div className="invite-right">
                <div className="invite-controls event-invite-controls event-invite-controls--stacked">
                  <div className="invite-search event-search">
                    <input
                      type="text"
                      className="search-input"
                      placeholder="Search..."
                      value={searchMember}
                      onChange={(e) => setSearchMember(e.target.value)}
                    />
                    <span className="invite-search-icon">
                      <img src="" />
                    </span>
                  </div>
                  <div className="event-invite-filters">
                  <div
                    ref={ageRangeRef}
                    className="selctions-option position-relative"
                    style={{ minWidth: "120px", width: "auto" }}
                  >
                    <div
                      className={filterTriggerClass(
                        "selection-drop text-nowrap",
                        Boolean(ageRangeFilterValue)
                      )}
                      style={{ fontWeight: "bold" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowAgeRange((prev) => !prev);
                      }}
                    >
                      <FilterTriggerLabel label="Age Range" value={ageRangeFilterValue} />
                    </div>

                    {showAgeRange && (
                      <div className="member-list" style={{ width: "180px", minWidth: "180px", padding: "10px" }}>
                        <div style={{ display: "flex", flexDirection: "row", gap: "6px", alignItems: "flex-end" }}>
                          <div style={{ display: "flex", flexDirection: "column", gap: "3px", flex: "1" }}>
                            <label style={{ fontSize: "11px", fontWeight: "500" }}>From</label>
                            <input
                              type="number"
                              value={ageFrom}
                              onChange={handleAgeFromChange}
                              placeholder="Age"
                              min="0"
                              style={{
                                padding: "4px 6px",
                                border: "1px solid #D9D9D9",
                                borderRadius: "4px",
                                fontSize: "12px",
                                width: "100%",
                                height: "28px"
                              }}
                            />
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", gap: "3px", flex: "1" }}>
                            <label style={{ fontSize: "11px", fontWeight: "500" }}>To</label>
                            <input
                              type="number"
                              value={ageTo}
                              onChange={handleAgeToChange}
                              placeholder="Age"
                              min={ageFrom && !isNaN(parseInt(ageFrom)) ? parseInt(ageFrom) + 1 : "0"}
                              style={{
                                padding: "4px 6px",
                                border: "1px solid #D9D9D9",
                                borderRadius: "4px",
                                fontSize: "12px",
                                width: "100%",
                                height: "28px"
                              }}
                            />
                          </div>
                        </div>

                        <div className="btn-container" style={{ display: "flex", gap: "6px", marginTop: "10px", paddingTop: "10px", borderTop: "1px solid #e0e0e0" }}>
                          <button
                            type="button"
                            className="btn btn-clear"
                            onClick={handleClearAgeRange}
                            style={{ flex: "1", padding: "4px 8px", fontSize: "12px", borderRadius: "4px", height: "28px" }}
                          >
                            Clear
                          </button>
                          <button
                            type="button"
                            className="btn btn-apply"
                            onClick={handleApplyAgeRange}
                            style={{ flex: "1", padding: "4px 8px", fontSize: "12px", borderRadius: "4px", height: "28px" }}
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div
                    ref={famRef}
                    className="selctions-option position-relative"
                  >
                    <div
                      className={filterTriggerClass(
                        "selection-drop family-color-drop",
                        Boolean(familyColorFilterValue)
                      )}
                      style={{ fontWeight: "bold" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowFamilyColor((prev) => {
                          const next = !prev;
                          if (next) fetchFamilyColorData();
                          return next;
                        });
                      }}
                    >
                      <FilterTriggerLabel label="Line/Color" value={familyColorFilterValue} />
                    </div>

                    {showFamilyColor && createPortal(
                      <div
                        ref={famPopupRef}
                        className="member-list family-color-popup"
                        style={{
                          position: "fixed",
                          left: famPopupPos?.left ?? 0,
                          top: famPopupPos?.top ?? 0,
                          right: "auto",
                          margin: 0,
                          width: "300px",
                          maxWidth: "calc(100vw - 16px)",
                          maxHeight: "calc(100dvh - 16px)",
                          display: "flex",
                          flexDirection: "column",
                          zIndex: 10000,
                          visibility: famPopupPos ? "visible" : "hidden",
                          boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
                        }}
                      >
                        <div
                          onPointerDown={beginFamPopupDrag}
                          onPointerMove={moveFamPopup}
                          onPointerUp={endFamPopupDrag}
                          onPointerCancel={endFamPopupDrag}
                          title="Drag to move"
                          style={{
                            cursor: "move",
                            touchAction: "none",
                            userSelect: "none",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "6px 15px",
                            borderBottom: "1px solid #e0e0e0",
                            fontSize: "13px",
                            fontWeight: 600,
                            color: "#555",
                            flexShrink: 0,
                          }}
                        >
                          <span>Line/Color</span>
                          <span aria-hidden="true" style={{ letterSpacing: "1px", color: "#999" }}>⠿</span>
                        </div>
                        <div style={{ overflowY: "auto", flex: "1 1 auto", minHeight: 0 }}>
                        {familyColorList.length === 0 ? (
                          <p>There is no family color</p>
                        ) : (
                          familyColorList.map((color, index) => {
                              const headOfLineName = formatDashboardFamilyLineHeadName(color);
                              
                              const isChecked = selectedFamilyColor.includes(color.hex_code);
                              
                              return (
                                <div className="member-item" key={index}>
                                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                    <input
                                      type="checkbox"
                                      checked={isChecked}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          // Add to selection
                                          setSelectedFamilyColor([...selectedFamilyColor, color.hex_code]);
                                        } else {
                                          // Remove from selection
                                          setSelectedFamilyColor(
                                            selectedFamilyColor.filter((hex) => hex !== color.hex_code)
                                          );
                                        }
                                      }}
                                    />
                                    <div style={{ flex: "1" }}>
                                      {renderNameWithColor(
                                        headOfLineName || "Start/Top of Line",
                                        color?.hex_code || "#000000",
                                        color?.color_name ? `${color.color_name} Line` : color?.name ? `${color.name} Line` : ""
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })
                        )}
                        </div>

                        <div className="btn-container" style={{ display: "flex", gap: "8px", padding: "10px 15px", borderTop: "1px solid #e0e0e0", flexShrink: 0 }}>
                          <button
                            type="button"
                            className="btn btn-clear"
                            onClick={handleClearFamilyColor}
                            style={{ flex: "1", padding: "6px 12px", fontSize: "14px", borderRadius: "4px" }}
                          >
                            Clear
                          </button>
                          <button
                            type="button"
                            className="btn btn-apply"
                            onClick={handleApplyFamilyColor}
                            style={{ flex: "1", padding: "6px 12px", fontSize: "14px", borderRadius: "4px" }}
                          >
                            Apply
                          </button>
                        </div>
                      </div>,
                      document.body
                    )}
                  </div>

                  <div
                    ref={genderRef}
                    className="selctions-option position-relative"
                    style={{ minWidth: "120px", width: "auto", flexShrink: 0 }}
                  >
                    <div
                      className={filterTriggerClass(
                        "selection-drop text-nowrap",
                        Boolean(genderFilterValue)
                      )}
                      style={{ fontWeight: "bold" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowGender((prev) => {
                          const next = !prev;
                          if (next) {
                            setSelectedGender(
                              appliedGender === "1" ||
                                appliedGender === "2" ||
                                appliedGender === "3"
                                ? appliedGender
                                : ""
                            );
                          }
                          return next;
                        });
                      }}
                    >
                      <FilterTriggerLabel label="Gender" value={genderFilterValue} />
                    </div>

                    {showGender && (
                      <div
                        className="member-list"
                        style={{
                          width: "200px",
                          minWidth: "180px",
                          padding: "10px",
                          zIndex: 2000,
                          background: "#fff",
                        }}
                      >
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                          <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", cursor: "pointer" }}>
                            <input
                              type="radio"
                              name="event-gender-filter"
                              checked={selectedGender === "1"}
                              onChange={() => setSelectedGender("1")}
                            />
                            Male
                          </label>
                          <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", cursor: "pointer" }}>
                            <input
                              type="radio"
                              name="event-gender-filter"
                              checked={selectedGender === "2"}
                              onChange={() => setSelectedGender("2")}
                            />
                            Female
                          </label>
                          <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", cursor: "pointer" }}>
                            <input
                              type="radio"
                              name="event-gender-filter"
                              checked={selectedGender === "3"}
                              onChange={() => setSelectedGender("3")}
                            />
                            Other
                          </label>
                        </div>

                        <div
                          className="btn-container"
                          style={{
                            display: "flex",
                            gap: "6px",
                            marginTop: "10px",
                            paddingTop: "10px",
                            borderTop: "1px solid #e0e0e0",
                          }}
                        >
                          <button
                            type="button"
                            className="btn btn-clear"
                            onClick={handleClearGender}
                            style={{
                              flex: "1",
                              padding: "4px 8px",
                              fontSize: "12px",
                              borderRadius: "4px",
                              height: "28px",
                            }}
                          >
                            Clear
                          </button>
                          <button
                            type="button"
                            className="btn btn-apply"
                            onClick={handleApplyGender}
                            style={{
                              flex: "1",
                              padding: "4px 8px",
                              fontSize: "12px",
                              borderRadius: "4px",
                              height: "28px",
                            }}
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div
                    ref={selectionRef}
                    className="selctions-option position-relative"
                    style={{ minWidth: "120px", width: "auto", flexShrink: 0 }}
                  >
                    <div
                      className={filterTriggerClass(
                        "selection-drop text-nowrap",
                        Boolean(selectionFilterValue)
                      )}
                      style={{ fontWeight: "bold" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowSelection((prev) => {
                          const next = !prev;
                          if (next) {
                            setSelectedSelectionFilter(
                              appliedSelectionFilter === "selected" ||
                                appliedSelectionFilter === "unselected"
                                ? appliedSelectionFilter
                                : ""
                            );
                          }
                          return next;
                        });
                      }}
                    >
                      <FilterTriggerLabel label="Selected" value={selectionFilterValue} />
                    </div>

                    {showSelection && (
                      <div
                        className="member-list"
                        style={{
                          width: "220px",
                          minWidth: "200px",
                          padding: "10px",
                          zIndex: 2000,
                          background: "#fff",
                        }}
                      >
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                          <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", cursor: "pointer" }}>
                            <input
                              type="radio"
                              name="event-selection-filter"
                              checked={selectedSelectionFilter === "selected"}
                              onChange={() => setSelectedSelectionFilter("selected")}
                            />
                            Selected ({selectedMembers.length})
                          </label>
                          <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", cursor: "pointer" }}>
                            <input
                              type="radio"
                              name="event-selection-filter"
                              checked={selectedSelectionFilter === "unselected"}
                              onChange={() => setSelectedSelectionFilter("unselected")}
                            />
                            Not Selected
                          </label>
                        </div>

                        <div
                          className="btn-container"
                          style={{
                            display: "flex",
                            gap: "6px",
                            marginTop: "10px",
                            paddingTop: "10px",
                            borderTop: "1px solid #e0e0e0",
                          }}
                        >
                          <button
                            type="button"
                            className="btn btn-clear"
                            onClick={handleClearSelectionFilter}
                            style={{
                              flex: "1",
                              padding: "4px 8px",
                              fontSize: "12px",
                              borderRadius: "4px",
                              height: "28px",
                            }}
                          >
                            Clear
                          </button>
                          <button
                            type="button"
                            className="btn btn-apply"
                            onClick={handleApplySelectionFilter}
                            style={{
                              flex: "1",
                              padding: "4px 8px",
                              fontSize: "12px",
                              borderRadius: "4px",
                              height: "28px",
                            }}
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  </div>
                  {/* <button className="btn invite-btn">Invite</button> */}
                </div>
              </div>

              <div className="customer-tablle-main event-invite-table">
              <div className="table-scroll-container">
              <table className="event-invite-members-table">
                  <colgroup>
                    <col style={{ width: 32 }} />
                    <col style={{ width: showEmailColumn ? "16%" : "40%" }} />
                    <col style={{ width: showEmailColumn ? "32%" : "60%" }} />
                    {showEmailColumn && <col style={{ width: "52%" }} />}
                  </colgroup>
                  <thead>
                    <tr>
                      <th
                        style={{
                          width: 32,
                          maxWidth: 32,
                          padding: "4px 2px 4px 8px",
                          verticalAlign: "middle",
                          boxSizing: "border-box",
                        }}
                      >
                        <input
                          type="checkbox"
                          disabled={isView}
                          checked={
                            filteredMembers.length > 0 &&
                            filteredMembers.every((mem) =>
                              isMemberInSelection(selectedMembers, mem)
                            )
                          }
                          onChange={(e) => {
                            if (e.target.checked) {
                              // Select all filtered members
                              const newSelections = [...selectedMembers];
                              filteredMembers.forEach((mem) => {
                                const memberUserId = mem.user_id || mem.user?.user_id;
                                const memberId = mem.id;
                                const exists = newSelections.some((m) => {
                                  const mUserId = m.user_id || m.user?.user_id;
                                  const mId = m.id;
                                  return (
                                    (memberUserId && mUserId && memberUserId === mUserId) ||
                                    (memberId && mId && memberId === mId)
                                  );
                                });
                                if (!exists) {
                                  newSelections.push({
                                    ...mem,
                                    user_id: memberUserId || mem.user_id,
                                    id: memberId || memberUserId || mem.id,
                                  });
                                }
                              });
                              setSelectedMembers(newSelections);
                            } else {
                              // Deselect filtered members except those already invited
                              setSelectedMembers((prev) =>
                                prev.filter((m) => {
                                  const inFilteredList = filteredMembers.some((mem) =>
                                    isMemberInSelection([m], mem)
                                  );
                                  if (!inFilteredList) return true;
                                  return isMemberInitiallyInvited(m);
                                })
                              );
                            }
                          }}
                        />
                      </th>
                      <th 
                        onClick={() => handleSort('name')}
                        style={{ cursor: 'pointer', userSelect: 'none', padding: '4px 12px 4px 6px', width: 'auto' }}
                      >
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                          <span>Name(s) ({filteredMembers.length})</span>
                          {sortBy === 'name' ? (
                            <span style={{ fontSize: '12px', color: '#025FEB', lineHeight: '1' }}>
                              {sortOrder === 'asc' ? '↑' : '↓'}
                            </span>
                          ) : (
                            <span style={{ fontSize: '10px', opacity: 0.5, color: '#666', lineHeight: '1', display: 'inline-block', verticalAlign: 'middle' }}>
                              <span style={{ display: 'block', lineHeight: '0.7', marginBottom: '-2px' }}>↑</span>
                              <span style={{ display: 'block', lineHeight: '0.7' }}>↓</span>
                            </span>
                          )}
                        </span>
                      </th>
                      <th 
                        onClick={() => handleSort('familyLine')}
                        style={{ cursor: 'pointer', userSelect: 'none', padding: '4px 12px 4px 6px', width: 'auto' }}
                      >
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                          <span>Family Line/Color</span>
                          {sortBy === 'familyLine' ? (
                            <span style={{ fontSize: '12px', color: '#025FEB', lineHeight: '1' }}>
                              {sortOrder === 'asc' ? '↑' : '↓'}
                            </span>
                          ) : (
                            <span style={{ fontSize: '10px', opacity: 0.5, color: '#666', lineHeight: '1', display: 'inline-block', verticalAlign: 'middle' }}>
                              <span style={{ display: 'block', lineHeight: '0.7', marginBottom: '-2px' }}>↑</span>
                              <span style={{ display: 'block', lineHeight: '0.7' }}>↓</span>
                            </span>
                          )}
                        </span>
                      </th>
                      {showEmailColumn && (
                        <th className="invite-email-col" style={{ padding: '4px 12px', width: 'auto' }}>Email</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMembers.length > 0 ? (
                      filteredMembers.map((mem, index) => {
                        return (
                          <tr key={mem.id || index} style={{ padding: 0 }}>
                            {/*CheckBox */}
                            <td
                              style={{
                                width: 32,
                                maxWidth: 32,
                                padding: "4px 2px 4px 8px",
                                verticalAlign: "middle",
                                boxSizing: "border-box",
                              }}
                            >
                              <label style={{ margin: 0, display: "inline-flex" }}>
                                <input
                                  type="checkbox"
                                  checked={isMemberInSelection(
                                    selectedMembers,
                                    mem
                                  )}
                                  disabled={
                                    isView || isMemberInitiallyInvited(mem)
                                  }
                                  onChange={() => toggleMemberSelection(mem)}
                                />
                              </label>
                            </td>

                            {/*Name */}
                            <td style={{ padding: "4px 12px 4px 6px" }}>
                              <div className="custom-table-profile">
                                <div className="">
                                  <h6 className="mb-0">{formatPersonName(mem)}</h6>
                                </div>
                              </div>
                            </td>

                            {/*Relation Parent info */}
                            <td style={{ padding: "4px 12px 4px 6px" }}>
                              {renderNameWithColor(
                                getHeadOfLineName(mem) || "Start/Top of Line",
                                mem?.hex_code || "#000000",
                                mem?.color_name ? `${mem.color_name} Line` : ""
                              )}
                            </td>

                            {/* Email (admin only) */}
                            {showEmailColumn && (
                              <td className="invite-email-col" style={{ padding: '4px 12px' }}>
                                <span>
                                  {mem?.email || mem?.user?.email || "-"}
                                </span>
                              </td>
                            )}
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td
                          colSpan={showEmailColumn ? 4 : 3}
                          className="text-center py-3 text-gray-500"
                        >
                          No members found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                </div>
              </div>
            </div>
            <div className="event-form-actions">
          <button type="button" className="event-btn-cancel" onClick={onClose}>
            {isView ? "Close" : "Cancel"}
          </button>
          {!isView && (
            <>
              {isCancelledEvent && (
                <button
                  type="button"
                  className="event-btn-reset"
                  onClick={handleResetCancelledForm}
                  disabled={loading || draftLoading}
                >
                  Reset
                </button>
              )}
              {editMode === "Add" && (
                <button
                  type="button"
                  className="event-btn-draft"
                  onClick={handleSaveDraft}
                  disabled={loading || draftLoading}
                >
                  {draftLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    "Save as Draft"
                  )}
                </button>
              )}
              <button
                type="submit"
                className="event-btn-save"
                disabled={draftLoading}
                title="Review invitation & invitee selections before sending."
              >
                {/* {editMode === "Edit"
                ? "Update & Send Invitations"
                : "Save & Send Invitations"} */}

                {loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : editMode === "Edit" ? (
                  "Send Invitations"
                ) : (
                  "Send invitation"
                )}
              </button>
            </>
          )}
        </div>
          </div>
        </div>

   
      </form>
    </>
  );
}

export default CreateEventModal;
